/**
 * Centralized Document Print Template Engine
 * Supports 4 template styles and 9 document types with per-company configuration.
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getCached } from './fyDataCache';
import { formatAmount } from './documentNumbering';
import { formatDate } from './formatters';
import { openPrintPreview, printHtmlInNewWindow } from './printPreviewBus';
import {
  isPrintBrandHidden, printBrandCss, printBrandEndHtml, printBrandLetterheadCss, printBrandLetterheadHtml,
  printBrandHalvesCss, printBrandHalfHtml,
} from './printBranding';
import { lineDiscountSummary } from './purchaseDiscount';
import { isModuleEnabledForCompany } from '../config/moduleRegistry';

// Printed info fields are a mix of dates and plain text (NTN, terms, names…).
// Format ONLY values that look like a stored ISO date (YYYY-MM-DD…) so the
// company's chosen date format applies to printed dates and nothing else.
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;
const fmtIfDate = (value) =>
  (typeof value === 'string' && ISO_DATE_RE.test(value)) ? formatDate(value) : value;

// Minimal HTML escape for user-entered strings injected into the print markup
// (e.g. the per-document custom title). Company-scoped, but escape anyway.
const escHtml = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// Amount in words (Indian/Pakistani numbering: crore / lakh / thousand) for the
// optional "Amount in Words" row on the Sale Invoice.
const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
const below1000 = (n) => {
  let s = '';
  if (n >= 100) { s += ONES[Math.floor(n / 100)] + ' Hundred'; n %= 100; if (n > 0) s += ' and '; }
  if (n >= 20) { s += TENS[Math.floor(n / 10)]; n %= 10; if (n > 0) s += ' ' + ONES[n]; }
  else if (n > 0) s += ONES[n];
  return s;
};
const amountToWords = (num) => {
  const v = Number(num);
  if (!isFinite(v)) return '';
  const neg = v < 0;
  let n = Math.floor(Math.abs(v));
  const paisa = Math.round((Math.abs(v) - n) * 100);
  if (n === 0 && paisa === 0) return 'Zero Only';
  const parts = [];
  const units = [[10000000, 'Crore'], [100000, 'Lakh'], [1000, 'Thousand']];
  for (const [div, name] of units) {
    if (n >= div) { parts.push(below1000(Math.floor(n / div)) + ' ' + name); n %= div; }
  }
  if (n > 0) parts.push(below1000(n));
  let out = parts.join(' ').trim();
  if (paisa > 0) out += ` and ${below1000(paisa)} Paisa`;
  return (neg ? 'Minus ' : '') + out + ' Only';
};

// ─── Default Configuration ────────────────────────────────────────────────────

const DEFAULT_GLOBAL = {
  template: 'classic',
  accentColor: '#800020',
  logoPosition: 'left',
  showCompanyPhone: true,
  showCompanyEmail: true,
  showCompanyNTN: true,
  showCompanyGST: true,
  footerText: '',
  showSignatures: false,
  signatureLabels: ['Prepared By', 'Approved By', 'Received By'],
  duplicateCopy: false,
  // 'top' renders the Notes block ABOVE the line-item table; 'bottom' (default)
  // keeps the historical layout where Notes appears in the footer below totals.
  // Terms & Conditions and Reason always remain in the footer.
  notesPosition: 'bottom',
};

const DEFAULT_DOC_OVERRIDES = {
  // Sale Invoice carries the full per-field show/hide set (Settings → Print
  // Formats → Sale Invoice). Every flag defaults to the CURRENT printed output,
  // so existing companies see no change until they switch something off. The
  // three genuinely new rows (total qty, amount in words, previous balance)
  // default to OFF.
  saleInvoice: {
    template: null,
    columns: {
      discountPercent: true, tax: true,
      hsCode: true, uom: true, rate: true, mrp: true, image: true, sourceDoc: true,
      // New optional column → OFF by default so existing prints don't change.
      barcode: false,
    },
    showPaymentInfo: true, showWHT: true, showTransportation: true, showOtherCharges: true, showAdvancePayment: true,
    // Header / info fields
    showDueDate: true, showPaymentTerms: true, showRefDocument: true,
    showCustomerPO: true, showNTN: true, showSTRN: true,
    showCustomerAddress: true, showCustomerPhone: true, showCustomerArea: true, showCustomerRegion: true,
    // Totals
    showSubtotal: true, showTotalQty: false, showAmountInWords: false,
    // Extra blocks
    showPreviousBalance: false,
  },
  purchaseInvoice: { template: null, columns: { discountPercent: true, tax: true }, showPaymentInfo: true, showWHT: true, showTransportation: true, showOtherCharges: true },
  nonTaxSaleInvoice: { template: null, columns: { discountPercent: true, tax: false }, showPaymentInfo: true, showWHT: false, showTax: false, showTransportation: true, showOtherCharges: true, showAdvancePayment: true, showCustomerAddress: true, showCustomerPhone: true, showCustomerArea: true, showCustomerRegion: true },
  nonTaxPurchaseInvoice: { template: null, columns: { discountPercent: true, tax: false }, showPaymentInfo: true, showWHT: true, showTax: false, showTransportation: true, showOtherCharges: true },
  saleOrder: { template: null, columns: { discountPercent: true, tax: true }, showPaymentInfo: false, showWHT: false },
  purchaseOrder: { template: null, columns: { discountPercent: true, tax: true }, showPaymentInfo: false, showWHT: false },
  quotation: { template: null, columns: { discountPercent: true, tax: true }, showPaymentInfo: false, showWHT: false, splitStoresServices: false },
  grn: { template: null, columns: {}, showPaymentInfo: false, showWHT: false },
  deliveryChallan: { template: null, columns: {}, showPaymentInfo: false, showWHT: false },
  saleReturn: { template: null, columns: { tax: true }, showPaymentInfo: false, showWHT: false },
  purchaseReturn: { template: null, columns: { tax: true }, showPaymentInfo: false, showWHT: true },
  nonTaxSaleReturn: { template: null, columns: { tax: false }, showPaymentInfo: false, showWHT: false, showTax: false },
  nonTaxPurchaseReturn: { template: null, columns: { tax: false }, showPaymentInfo: false, showWHT: true, showTax: false },
  importPurchaseInvoice: { template: null, columns: {}, showPaymentInfo: true, showWHT: false, showTax: false, showCharges: true, showLandedSummary: true },
  payslip: { template: null, columns: {}, showPaymentInfo: false, showWHT: false, showTax: false },
  salaryAdvance: { template: null, columns: {}, showPaymentInfo: false, showWHT: false, showTax: false },
  staffLoan: { template: null, columns: {}, showPaymentInfo: false, showWHT: false, showTax: false },
  loanRecovery: { template: null, columns: {}, showPaymentInfo: false, showWHT: false, showTax: false },
  finalSettlement: { template: null, columns: {}, showPaymentInfo: false, showWHT: false, showTax: false },
  addStock: { template: null, columns: {}, showPaymentInfo: false, showWHT: false },
  reduceStock: { template: null, columns: {}, showPaymentInfo: false, showWHT: false },
  stockTransfer: { template: null, columns: {}, showPaymentInfo: false, showWHT: false },
  branchTransfer: { template: null, columns: {}, showPaymentInfo: false, showWHT: false },
  purchaseRequisition: { template: null, columns: {}, showPaymentInfo: false, showWHT: false },
  inwardGatePass: { template: null, columns: {}, showPaymentInfo: false, showWHT: false },
  outwardGatePass: { template: null, columns: {}, showPaymentInfo: false, showWHT: false },
  billOfMaterials: { template: null, columns: {}, showPaymentInfo: false, showWHT: false },
  productionOrder: { template: null, columns: {}, showPaymentInfo: false, showWHT: false },
  materialIssue: { template: null, columns: {}, showPaymentInfo: false, showWHT: false },
  productionReceipt: { template: null, columns: {}, showPaymentInfo: false, showWHT: false },
};

// ─── Document Type Metadata ───────────────────────────────────────────────────

const DOC_META = {
  saleInvoice: {
    title: 'SALE INVOICE',
    partyLabel: 'Customer',
    // cfgKey → a per-document show/hide toggle in Print Settings. No cfgKey
    // means the field is always printed (Customer can't be hidden).
    infoFields: (d) => [
      // Walk-in invoices may carry a per-invoice name/phone (stored on the doc
      // only). When present, the name replaces the generic "Walk-in Customer" and
      // the phone prints beneath it; otherwise this is just the party name.
      { label: 'Customer', value: d.walkInName || d.partyName },
      { label: 'Address', value: d.partyAddress, hideIfEmpty: true, cfgKey: 'showCustomerAddress' },
      { label: 'Phone', value: d.walkInPhone || d.partyPhone, hideIfEmpty: true, cfgKey: 'showCustomerPhone' },
      { label: 'Area', value: d.partyArea, hideIfEmpty: true, cfgKey: 'showCustomerArea' },
      { label: 'Region', value: d.partyRegion, hideIfEmpty: true, cfgKey: 'showCustomerRegion' },
      { label: 'NTN', value: d.partyNTN, hideIfEmpty: true, cfgKey: 'showNTN' },
      { label: 'STRN', value: d.partySTRN, hideIfEmpty: true, cfgKey: 'showSTRN' },
      { label: 'Ref Document', value: d.parentDocNumber || 'Direct', cfgKey: 'showRefDocument' },
      { label: 'Customer PO', value: d.customerPO, hideIfEmpty: true, cfgKey: 'showCustomerPO' },
      { label: 'Due Date', value: d.dueDate, cfgKey: 'showDueDate' },
      { label: 'Payment Terms', value: d.paymentTerms, cfgKey: 'showPaymentTerms' },
    ],
    columnSet: 'standard',
    totalsType: 'invoice',
  },
  purchaseInvoice: {
    title: 'PURCHASE INVOICE',
    partyLabel: 'Vendor',
    infoFields: (d) => [
      { label: 'Vendor', value: d.partyName },
      { label: 'Vendor Invoice No.', value: d.vendorInvoiceNo },
      { label: 'Vendor Invoice Date', value: d.vendorInvoiceDate },
      { label: 'GRN Ref', value: d.parentDocNumber || 'Direct' },
      { label: 'PO Ref', value: d.poNumber },
      { label: 'Due Date', value: d.dueDate },
      { label: 'Payment Terms', value: d.paymentTerms },
    ],
    columnSet: 'standard',
    totalsType: 'invoice',
  },
  nonTaxSaleInvoice: {
    title: 'NON-TAX SALE INVOICE',
    partyLabel: 'Customer',
    infoFields: (d) => [
      // Walk-in invoices may carry a per-invoice name/phone (see saleInvoice).
      { label: 'Customer', value: d.walkInName || d.partyName },
      { label: 'Address', value: d.partyAddress, hideIfEmpty: true, cfgKey: 'showCustomerAddress' },
      { label: 'Phone', value: d.walkInPhone || d.partyPhone, hideIfEmpty: true, cfgKey: 'showCustomerPhone' },
      { label: 'Area', value: d.partyArea, hideIfEmpty: true, cfgKey: 'showCustomerArea' },
      { label: 'Region', value: d.partyRegion, hideIfEmpty: true, cfgKey: 'showCustomerRegion' },
      // Same show/hide switches as the Sale Invoice (2026-11-02); unset = printed as before.
      { label: 'NTN', value: d.partyNTN, hideIfEmpty: true, cfgKey: 'showNTN' },
      { label: 'STRN', value: d.partySTRN, hideIfEmpty: true, cfgKey: 'showSTRN' },
      { label: 'Ref Document', value: d.parentDocNumber || 'Direct', cfgKey: 'showRefDocument' },
      { label: 'Customer PO', value: d.customerPO, hideIfEmpty: true, cfgKey: 'showCustomerPO' },
      { label: 'Due Date', value: d.dueDate, cfgKey: 'showDueDate' },
      { label: 'Payment Terms', value: d.paymentTerms, cfgKey: 'showPaymentTerms' },
    ],
    columnSet: 'standard',
    totalsType: 'invoice',
  },
  nonTaxPurchaseInvoice: {
    title: 'NON-TAX PURCHASE INVOICE',
    partyLabel: 'Vendor',
    infoFields: (d) => [
      { label: 'Vendor', value: d.partyName },
      { label: 'Vendor Invoice No.', value: d.vendorInvoiceNo },
      { label: 'Vendor Invoice Date', value: d.vendorInvoiceDate },
      { label: 'GRN Ref', value: d.parentDocNumber || 'Direct' },
      { label: 'PO Ref', value: d.poNumber },
      { label: 'Due Date', value: d.dueDate },
      { label: 'Payment Terms', value: d.paymentTerms },
    ],
    columnSet: 'standard',
    totalsType: 'invoice',
  },
  saleOrder: {
    title: 'SALE ORDER',
    partyLabel: 'Customer',
    infoFields: (d) => [
      { label: 'Customer', value: d.partyName },
      { label: 'NTN', value: d.partyNTN, hideIfEmpty: true },
      { label: 'STRN', value: d.partySTRN, hideIfEmpty: true },
      { label: 'Customer PO', value: d.customerPO },
      { label: 'Quotation Ref', value: d.parentDocNumber },
      { label: 'Delivery Date', value: d.deliveryDate },
      { label: 'Delivery Address', value: d.deliveryAddress },
      { label: 'Location', value: d.locationName },
    ],
    columnSet: 'standard',
    totalsType: 'order',
  },
  purchaseOrder: {
    title: 'PURCHASE ORDER',
    partyLabel: 'Vendor',
    infoFields: (d) => [
      { label: 'Vendor', value: d.partyName },
      { label: 'Reference No.', value: d.referenceNo },
      { label: 'Delivery Date', value: d.deliveryDate },
      { label: 'Payment Terms', value: d.paymentTerms },
      { label: 'Delivery Address', value: d.deliveryAddress },
      { label: 'Location', value: d.locationName },
    ],
    columnSet: 'standard',
    totalsType: 'order',
  },
  quotation: {
    title: 'QUOTATION',
    partyLabel: 'Customer',
    extraHeaderFields: (d) => [
      { label: 'Valid Until', value: d.validUntil },
    ],
    infoFields: (d) => [
      { label: 'Customer', value: d.partyName },
      { label: 'NTN', value: d.partyNTN, hideIfEmpty: true },
      { label: 'STRN', value: d.partySTRN, hideIfEmpty: true },
      { label: 'Customer Ref', value: d.customerRef },
      { label: 'Location', value: d.locationName },
    ],
    columnSet: 'standard',
    totalsType: 'order',
  },
  grn: {
    title: 'GOODS RECEIPT NOTE',
    partyLabel: 'Vendor',
    infoFields: (d) => [
      { label: 'Vendor', value: d.partyName },
      { label: 'PO Number', value: d.parentDocNumber || 'Direct GRN' },
      { label: 'Location', value: d.locationName },
      { label: 'Vehicle No.', value: d.vehicleNo },
    ],
    columnSet: 'grn',
    totalsType: 'simple',
  },
  deliveryChallan: {
    title: 'DELIVERY CHALLAN',
    partyLabel: 'Customer',
    infoFields: (d) => [
      { label: 'Customer', value: d.partyName },
      { label: 'NTN', value: d.partyNTN, hideIfEmpty: true },
      { label: 'STRN', value: d.partySTRN, hideIfEmpty: true },
      { label: 'Sale Order Ref', value: d.parentDocNumber },
      { label: 'Customer PO', value: d.customerPO, hideIfEmpty: true },
      { label: 'Vehicle No', value: d.vehicleNo },
      { label: 'Driver Name', value: d.driverName },
      { label: 'Driver Phone', value: d.driverPhone },
      { label: 'Delivery Address', value: d.deliveryAddress },
    ],
    columnSet: 'dc',
    totalsType: 'dc',
    defaultSignatures: true,
    defaultSignatureLabels: ['Prepared By', 'Driver Signature', 'Received By'],
  },
  saleReturn: {
    title: 'SALE RETURN (CREDIT NOTE)',
    partyLabel: 'Customer',
    infoFields: (d) => [
      { label: 'Customer', value: d.partyName },
      { label: 'NTN', value: d.partyNTN, hideIfEmpty: true },
      { label: 'STRN', value: d.partySTRN, hideIfEmpty: true },
      { label: 'Original Invoice', value: d.parentDocNumber },
      { label: 'Reason for Return', value: d.reason },
    ],
    columnSet: 'return',
    totalsType: 'return',
  },
  purchaseReturn: {
    title: 'PURCHASE RETURN (DEBIT NOTE)',
    partyLabel: 'Vendor',
    infoFields: (d) => [
      { label: 'Vendor', value: d.partyName },
      { label: 'Original Invoice', value: d.parentDocNumber },
      { label: 'Reason for Return', value: d.reason },
    ],
    columnSet: 'return',
    totalsType: 'returnWht',
  },
  nonTaxSaleReturn: {
    title: 'NON-TAX SALE RETURN (CREDIT NOTE)',
    partyLabel: 'Customer',
    infoFields: (d) => [
      { label: 'Customer', value: d.partyName },
      { label: 'NTN', value: d.partyNTN, hideIfEmpty: true },
      { label: 'STRN', value: d.partySTRN, hideIfEmpty: true },
      { label: 'Original Invoice', value: d.parentDocNumber },
      { label: 'Reason for Return', value: d.reason },
    ],
    columnSet: 'return',
    totalsType: 'return',
  },
  nonTaxPurchaseReturn: {
    title: 'NON-TAX PURCHASE RETURN (DEBIT NOTE)',
    partyLabel: 'Vendor',
    infoFields: (d) => [
      { label: 'Vendor', value: d.partyName },
      { label: 'Original Invoice', value: d.parentDocNumber },
      { label: 'Reason for Return', value: d.reason },
    ],
    columnSet: 'return',
    totalsType: 'returnWht',
  },
  salaryAdvance: {
    title: 'SALARY ADVANCE VOUCHER',
    partyLabel: 'Staff',
    infoFields: (d) => [
      { label: 'Staff', value: d.partyName },
      { label: 'Reason', value: d.reason },
      { label: 'Pay From', value: `${(d.paymentMethod || 'cash').toUpperCase()} — ${d.paymentAccountName || ''}` },
      { label: 'Amount', value: `${d.totalAmount ? Number(d.totalAmount).toLocaleString() : 0}` },
      { label: 'Recovered', value: `${d.recoveredAmount ? Number(d.recoveredAmount).toLocaleString() : 0}` },
      { label: 'Outstanding', value: `${(d.outstandingAmount ?? d.totalAmount) ? Number(d.outstandingAmount ?? d.totalAmount).toLocaleString() : 0}` },
    ],
    columnSet: 'voucher',
    totalsType: 'simple',
    defaultSignatures: true,
    defaultSignatureLabels: ['Issued By', 'Approved By', 'Received By (Staff)'],
  },
  staffLoan: {
    title: 'STAFF LOAN AGREEMENT',
    partyLabel: 'Staff',
    infoFields: (d) => [
      { label: 'Staff', value: d.partyName },
      { label: 'Purpose', value: d.purpose },
      { label: 'Disbursed From', value: `${(d.paymentMethod || 'bank').toUpperCase()} — ${d.paymentAccountName || ''}` },
      { label: 'Principal', value: `${d.totalAmount ? Number(d.totalAmount).toLocaleString() : 0}` },
      { label: '# Installments', value: d.numberOfInstallments },
      { label: 'Monthly Installment', value: `${d.monthlyInstallment ? Number(d.monthlyInstallment).toLocaleString() : 0}` },
      { label: 'First Installment Month', value: d.firstInstallmentMonth },
      { label: 'Paid Installments', value: `${d.paidInstallments || 0} / ${d.numberOfInstallments || 0}` },
      { label: 'Recovered', value: `${d.recoveredAmount ? Number(d.recoveredAmount).toLocaleString() : 0}` },
      { label: 'Outstanding', value: `${(d.outstandingAmount ?? d.totalAmount) ? Number(d.outstandingAmount ?? d.totalAmount).toLocaleString() : 0}` },
    ],
    columnSet: 'voucher',
    totalsType: 'simple',
    defaultSignatures: true,
    defaultSignatureLabels: ['Disbursed By', 'Approved By', 'Borrower (Staff)'],
  },
  loanRecovery: {
    title: 'LOAN RECOVERY RECEIPT',
    partyLabel: 'Staff',
    infoFields: (d) => [
      { label: 'Staff', value: d.partyName },
      { label: 'Received In', value: `${(d.paymentMethod || 'cash').toUpperCase()} — ${d.paymentAccountName || ''}` },
      { label: 'Amount Received', value: `${d.totalAmount ? Number(d.totalAmount).toLocaleString() : 0}` },
      { label: 'Reference', value: d.reference || '' },
    ],
    columnSet: 'loanRecovery',
    totalsType: 'simple',
    defaultSignatures: true,
    defaultSignatureLabels: ['Received By', 'Approved By', 'Paid By (Staff)'],
  },
  finalSettlement: {
    title: 'FULL & FINAL SETTLEMENT',
    partyLabel: 'Staff',
    infoFields: (d) => [
      { label: 'Staff', value: d.partyName },
      { label: 'Last Working Day', value: d.lastWorkingDay },
      { label: 'Total Earnings', value: `${d.totalEarnings ? Number(d.totalEarnings).toLocaleString() : 0}` },
      { label: 'Loan Recovery', value: `${d.loanRecovery ? Number(d.loanRecovery).toLocaleString() : 0}` },
      { label: 'Advance Recovery', value: `${d.advanceRecovery ? Number(d.advanceRecovery).toLocaleString() : 0}` },
      { label: 'Total Deductions', value: `${d.totalDeductions ? Number(d.totalDeductions).toLocaleString() : 0}` },
      { label: 'Net Payable', value: `${d.netPayable ? Number(d.netPayable).toLocaleString() : 0}` },
    ],
    columnSet: 'voucher',
    totalsType: 'simple',
    defaultSignatures: true,
    defaultSignatureLabels: ['Prepared By', 'Approved By', 'Received By (Staff)'],
  },
  payslip: {
    title: 'SALARY PAYSLIP',
    partyLabel: 'Staff',
    extraHeaderFields: (d) => [
      { label: 'Pay Period', value: (() => {
        if (!d.payrollMonth) return '';
        const [y, mn] = d.payrollMonth.split('-');
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${months[parseInt(mn, 10) - 1]} ${y}`;
      })() }
    ],
    infoFields: (d) => {
      const l = d.line || {};
      return [
        { label: 'Staff Name', value: l.staffName },
        { label: 'Staff Code', value: l.staffCode },
        { label: 'Designation', value: l.designation },
        { label: 'Department', value: l.department },
        { label: 'Working Days', value: l.totalWorkingDays },
        { label: 'Present', value: l.present },
        { label: 'Absent', value: l.absent },
        { label: 'Paid Leave', value: l.paidLeave },
        { label: 'Payable Days', value: l.payableDays },
      ];
    },
    columnSet: 'payslip',
    totalsType: 'payslip',
    defaultSignatures: true,
    defaultSignatureLabels: ['Prepared By', 'Approved By', 'Received By (Staff)'],
  },
  importPurchaseInvoice: {
    title: 'IMPORT PURCHASE INVOICE',
    partyLabel: 'Foreign Vendor',
    extraHeaderFields: (d) => [
      { label: 'Currency', value: `${d.foreignCurrency || 'USD'} @ ${d.exchangeRate || 0}` },
    ],
    infoFields: (d) => [
      { label: 'Foreign Vendor', value: d.partyName },
      { label: 'Vendor Invoice No.', value: d.vendorInvoiceNo },
      { label: 'Vendor Invoice Date', value: d.vendorInvoiceDate },
      { label: 'GD No.', value: d.gdNumber },
      { label: 'GD Date', value: d.gdDate },
      { label: 'BL / AWB No.', value: d.blAwbNumber },
      { label: 'Container No.', value: d.containerNumber },
      { label: 'Shipment Mode', value: d.shipmentMode },
      { label: 'Incoterms', value: d.incoterms },
      { label: 'Country of Origin', value: d.countryOfOrigin },
      { label: 'Port of Loading', value: d.portOfLoading },
      { label: 'Port of Discharge', value: d.portOfDischarge },
      { label: 'Estimated Arrival', value: d.estimatedArrival },
      { label: 'Actual Arrival', value: d.actualArrival },
    ],
    columnSet: 'import',
    totalsType: 'import',
  },
  // ── Store Module ──────────────────────────────────────────────────────────
  addStock: {
    title: 'ADD STOCK ADJUSTMENT',
    infoFields: (d) => [
      { label: 'Adjustment Type', value: d.adjustmentType },
      { label: 'Reason', value: d.reason },
      { label: 'Location', value: d.locationName },
      { label: 'Employee', value: d.employeeName },
    ],
    columnSet: 'stockAdjust',
    totalsType: 'simple',
  },
  reduceStock: {
    title: 'REDUCE STOCK ADJUSTMENT',
    infoFields: (d) => [
      { label: 'Adjustment Type', value: d.adjustmentType },
      { label: 'Reason', value: d.reason },
      { label: 'Location', value: d.locationName },
      { label: 'Employee', value: d.employeeName },
    ],
    columnSet: 'stockAdjust',
    totalsType: 'simple',
  },
  materialIssue: {
    title: 'MATERIAL ISSUE TO PRODUCTION',
    infoFields: (d) => [
      { label: 'From Location', value: d.locationName },
      { label: 'Employee', value: d.employeeName },
      { label: 'Job', value: d.jobName },
    ],
    columnSet: 'stockAdjust',
    totalsType: 'simple',
  },
  productionReceipt: {
    title: 'PRODUCTION RECEIPT',
    infoFields: (d) => [
      { label: 'From Material Issue', value: d.issueDocNumber },
      { label: 'Into Location', value: d.locationName },
      { label: 'Employee', value: d.employeeName },
      { label: 'Job', value: d.jobName },
    ],
    columnSet: 'stockAdjust',
    totalsType: 'simple',
  },
  stockTransfer: {
    title: 'STOCK TRANSFER',
    infoFields: (d) => [
      { label: 'From Location', value: d.fromLocationName },
      { label: 'To Location', value: d.toLocationName },
      { label: 'Employee', value: d.employeeName },
    ],
    columnSet: 'stockAdjust',
    totalsType: 'simple',
  },
  branchTransfer: {
    title: 'BRANCH TRANSFER',
    infoFields: (d) => [
      { label: 'From Branch', value: d.fromBranchName },
      { label: 'To Branch', value: d.toBranchName },
      { label: 'From Location', value: d.fromLocationName },
      { label: 'To Location', value: d.toLocationName },
      { label: 'Employee', value: d.employeeName },
    ],
    columnSet: 'stockAdjust',
    totalsType: 'simple',
  },
  purchaseRequisition: {
    title: 'PURCHASE REQUISITION',
    infoFields: (d) => [
      { label: 'Requested By', value: d.requestedBy },
      { label: 'Department', value: d.department },
      { label: 'Priority', value: d.priority },
      { label: 'Required Date', value: d.requiredDate },
      { label: 'Location', value: d.locationName },
    ],
    columnSet: 'requisition',
    totalsType: 'simple',
  },
  inwardGatePass: {
    title: 'INWARD GATE PASS',
    infoFields: (d) => [
      { label: 'Vendor', value: d.partyName },
      { label: 'Purpose', value: d.purpose },
      { label: 'Vehicle No', value: d.vehicleNo },
      { label: 'Driver', value: d.driverName },
      { label: 'Received By', value: d.receivedBy },
      { label: 'Time', value: d.docTime },
      { label: 'Location', value: d.locationName },
    ],
    columnSet: 'gatePass',
    totalsType: 'none',
    defaultSignatures: true,
    defaultSignatureLabels: ['Security / Receiver', 'Store Manager'],
  },
  outwardGatePass: {
    title: 'OUTWARD GATE PASS',
    infoFields: (d) => [
      { label: 'Destination', value: d.destination },
      { label: 'Purpose', value: d.purpose },
      { label: 'Vehicle No', value: d.vehicleNo },
      { label: 'Driver', value: d.driverName },
      { label: 'Authorized By', value: d.authorizedBy },
      { label: 'Time', value: d.docTime },
      { label: 'Location', value: d.locationName },
    ],
    columnSet: 'gatePass',
    totalsType: 'none',
    defaultSignatures: true,
    defaultSignatureLabels: ['Authorized By', 'Store Manager'],
  },
  billOfMaterials: {
    title: 'BILL OF MATERIALS',
    infoFields: (d) => [
      { label: 'BOM Name', value: d.bomName },
      { label: 'Finished Item', value: d.finishedItemName || '' },
      { label: 'Output Qty', value: `${d.outputQuantity || 1} ${d.outputUom || ''}` },
      { label: 'Production Type', value: d.productionType || 'In-House' },
      { label: 'Version', value: d.version },
      { label: 'Effective From', value: d.effectiveFrom },
      { label: 'Effective To', value: d.effectiveTo || '-' },
      { label: 'Cost/Unit', value: formatAmount(d.costPerUnit || 0) },
    ],
    columnSet: 'bom',
    totalsType: 'bomCost',
  },
  productionOrder: {
    title: 'PRODUCTION ORDER',
    infoFields: (d) => [
      { label: 'BOM', value: d.bomDocNumber || '' },
      { label: 'Finished Item', value: d.finishedItemName || '' },
      { label: 'Production Type', value: d.productionType || 'In-House' },
      { label: 'Planned Qty', value: `${d.plannedQty || 0} ${d.outputUom || ''}` },
      { label: 'Actual Qty', value: `${d.actualQty || 0} ${d.outputUom || ''}` },
      { label: 'Yield', value: d.plannedQty > 0 ? `${((d.actualQty || 0) / d.plannedQty * 100).toFixed(1)}%` : '-' },
      { label: 'Completion Date', value: d.completionDate || '-' },
      { label: 'Location', value: d.locationName || '-' },
    ],
    columnSet: 'production',
    totalsType: 'productionCost',
  },
  // ── Receipts / Payments / Journal Vouchers ───────────────────────────────
  cashReceipt: {
    title: 'CASH RECEIPT VOUCHER',
    partyLabel: 'Received From',
    infoFields: (d) => [
      { label: 'Received From', value: d.partyName },
      { label: 'Cash Account', value: d.paymentAccountName || d.cashAccountName },
      // Receipts: gross cleared from the customer (= cash when no WHT / bad debt).
      { label: 'Amount', value: formatAmount(receiptGrossOf(d)) },
      { label: 'Narration', value: d.narration || d.description || '' },
      { label: 'Reference', value: d.referenceNo || '' },
    ],
    columnSet: 'voucher',
    totalsType: 'simple',
    defaultSignatures: true,
    defaultSignatureLabels: ['Received By', 'Paid By', 'Approved By'],
  },
  bankReceipt: {
    title: 'BANK RECEIPT VOUCHER',
    partyLabel: 'Received From',
    infoFields: (d) => [
      { label: 'Received From', value: d.partyName },
      { label: 'Bank Account', value: d.paymentAccountName || d.bankAccountName },
      // Receipts: gross cleared from the customer (= cash when no WHT / bad debt).
      { label: 'Amount', value: formatAmount(receiptGrossOf(d)) },
      { label: 'Payment Mode', value: d.paymentMode || 'Bank Transfer' },
      { label: 'Cheque / Ref No.', value: d.chequeNo || d.referenceNo || '' },
      { label: 'Cheque / Ref Date', value: d.chequeDate || '' },
      { label: 'Narration', value: d.narration || d.description || '' },
    ],
    columnSet: 'voucher',
    totalsType: 'simple',
    defaultSignatures: true,
    defaultSignatureLabels: ['Received By', 'Paid By', 'Approved By'],
  },
  cashPayment: {
    title: 'CASH PAYMENT VOUCHER',
    partyLabel: 'Paid To',
    infoFields: (d) => [
      { label: 'Paid To', value: d.partyName },
      { label: 'Cash Account', value: d.paymentAccountName || d.cashAccountName },
      { label: 'Amount', value: formatAmount(d.totalAmount || d.amount || 0) },
      { label: 'Narration', value: d.narration || d.description || '' },
      { label: 'Reference', value: d.referenceNo || '' },
    ],
    columnSet: 'voucher',
    totalsType: 'simple',
    defaultSignatures: true,
    defaultSignatureLabels: ['Paid By', 'Received By', 'Approved By'],
  },
  bankPayment: {
    title: 'BANK PAYMENT VOUCHER',
    partyLabel: 'Paid To',
    infoFields: (d) => [
      { label: 'Paid To', value: d.partyName },
      { label: 'Bank Account', value: d.paymentAccountName || d.bankAccountName },
      { label: 'Amount', value: formatAmount(d.totalAmount || d.amount || 0) },
      { label: 'Payment Mode', value: d.paymentMode || 'Bank Transfer' },
      { label: 'Cheque / Ref No.', value: d.chequeNo || d.referenceNo || '' },
      { label: 'Cheque / Ref Date', value: d.chequeDate || '' },
      { label: 'Narration', value: d.narration || d.description || '' },
    ],
    columnSet: 'voucher',
    totalsType: 'simple',
    defaultSignatures: true,
    defaultSignatureLabels: ['Paid By', 'Received By', 'Approved By'],
  },
  journalVoucher: {
    title: 'JOURNAL VOUCHER',
    infoFields: (d) => [
      { label: 'Narration', value: d.narration || d.description || '' },
      { label: 'Reference', value: d.referenceNo || '' },
      { label: 'Total Debit', value: formatAmount(d.totalDebit || d.totalAmount || 0) },
      { label: 'Total Credit', value: formatAmount(d.totalCredit || d.totalAmount || 0) },
    ],
    columnSet: 'journal',
    totalsType: 'journal',
    defaultSignatures: true,
    defaultSignatureLabels: ['Prepared By', 'Checked By', 'Approved By'],
  },
};

// ─── Resolve Config ───────────────────────────────────────────────────────────

function resolveConfig(docTypeKey, savedConfig) {
  const globalCfg = { ...DEFAULT_GLOBAL, ...(savedConfig?.global || {}) };
  const docDefaults = DEFAULT_DOC_OVERRIDES[docTypeKey] || {};
  const docOverride = savedConfig?.[docTypeKey] || {};
  const letterhead = resolveLetterhead(docTypeKey, savedConfig);

  return {
    // Pass every per-document flag straight through (field show/hide toggles
    // etc.), defaults first then the company's saved overrides. The explicit
    // keys below still win where they compute something.
    ...docDefaults,
    ...docOverride,
    template: docOverride.template || globalCfg.template,
    // Per-document custom heading (e.g. "ESTIMATE INVOICE"). Blank = use the
    // standard DOC_META.title.
    customTitle: (docOverride.customTitle || '').trim(),
    accentColor: globalCfg.accentColor,
    logoPosition: globalCfg.logoPosition,
    showCompanyPhone: globalCfg.showCompanyPhone,
    showCompanyEmail: globalCfg.showCompanyEmail,
    showCompanyNTN: globalCfg.showCompanyNTN,
    showCompanyGST: globalCfg.showCompanyGST,
    // Multi-branch companies: print the branch name above the company name
    // (default) or only the company name (Print Settings → Show Branch Name).
    showBranchName: globalCfg.showBranchName !== false,
    footerText: globalCfg.footerText,
    showSignatures: globalCfg.showSignatures,
    signatureLabels: globalCfg.signatureLabels || DEFAULT_GLOBAL.signatureLabels,
    notesPosition: globalCfg.notesPosition === 'top' ? 'top' : 'bottom',
    columns: { ...docDefaults.columns, ...docOverride.columns },
    showPaymentInfo: docOverride.showPaymentInfo ?? docDefaults.showPaymentInfo ?? false,
    showWHT: docOverride.showWHT ?? docDefaults.showWHT ?? false,
    // The column-visibility toggles are the source of truth: hiding the Tax / Discount
    // column from line items must also hide the same row in the totals block. Explicit
    // showTax/showDiscount overrides win if set, otherwise we mirror the column flag.
    showTax: docOverride.showTax ?? docDefaults.showTax ?? (mergedCols(docDefaults, docOverride).tax !== false),
    showDiscount: docOverride.showDiscount ?? docDefaults.showDiscount ?? (mergedCols(docDefaults, docOverride).discountPercent !== false),
    // Duplicate copy and letterhead are mutually exclusive — letterhead images are per-page,
    // duplicate splits the page in half so the fixed-position images would overlap content.
    duplicateCopy: letterhead ? false : (globalCfg.duplicateCopy || false),
    letterhead,
    // Paper size (A4 | A5P | A5L). A5 is only honored for PLAIN invoices — if
    // letterhead or duplicate-copy is on we force A4 (their layouts assume A4).
    paperSize: (letterhead || globalCfg.duplicateCopy) ? 'A4' : (docOverride.paperSize || 'A4'),
    // Print the company's Trade Name as a tagline under the company name. Off by default.
    showTradeName: docOverride.showTradeName === true,
  };
}

// Letterhead is "active" for a doc type only when (a) the per-doc checkbox is on, and
// (b) at least one of header/footer/watermark image URLs is present. When inactive, the
// printed doc renders identically to before this feature existed.
function resolveLetterhead(docTypeKey, savedConfig) {
  const lh = savedConfig?.letterhead;
  if (!lh) return null;
  if (!lh.enabledForDocTypes?.[docTypeKey]) return null;
  const hasAnyImage = !!(lh.headerUrl || lh.footerUrl || lh.watermarkUrl);
  if (!hasAnyImage) return null;
  return {
    headerUrl: lh.headerUrl || null,
    footerUrl: lh.footerUrl || null,
    watermarkUrl: lh.watermarkUrl || null,
    headerHeightMm: clamp(Number(lh.headerHeightMm) || 45, 10, 100),
    footerHeightMm: clamp(Number(lh.footerHeightMm) || 30, 10, 80),
    watermarkOpacity: clamp(Number(lh.watermarkOpacity) || 0.08, 0.02, 0.5),
    watermarkWidthPct: clamp(Number(lh.watermarkWidthPct) || 60, 20, 100),
  };
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

// Helper: compute the effective columns map (defaults merged with user override).
function mergedCols(docDefaults, docOverride) {
  return { ...(docDefaults?.columns || {}), ...(docOverride?.columns || {}) };
}

// ─── CSS Templates ────────────────────────────────────────────────────────────

function getCSS(template, accent) {
  const base = `
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:'Segoe UI',sans-serif;color:#1A1A1A;line-height:1.4}
    @media print{body{margin:20px}@page{margin:15mm}}
    .company-tagline{font-style:italic;font-size:12px;color:#333;margin-top:1px}
  `;

  const templates = {
    classic: `${base}
      .print-page{margin:40px}
      .header{display:flex;justify-content:space-between;margin-bottom:30px;border-bottom:2px solid ${accent};padding-bottom:15px}
      .header-left p{margin:2px 0} .header-right{text-align:right}
      .company-name{font-weight:700;font-size:18px;color:${accent}}
      .company-detail{font-size:12px;color:#333}
      .doc-title{color:${accent};font-size:22px;font-weight:700;margin:0}
      .doc-number{font-size:18px;font-weight:600;margin:4px 0 0 0}
      .doc-meta{font-size:13px;margin:2px 0}
      .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:15px;margin-bottom:25px}
      .info-item label{font-weight:600;font-size:12px;color:#1A1A1A;display:block}
      .info-item span{font-size:14px}
      table.items{width:100%;border-collapse:collapse;margin-bottom:20px}
      table.items th{background:${accent};color:white;padding:10px 8px;text-align:left;font-size:12px}
      table.items td{padding:8px;border-bottom:1px solid #ddd;font-size:13px}
      table.items tr:nth-child(even){background:#f9f9f9}
      .totals{float:right;width:320px}
      .totals table{width:100%;margin-top:10px}
      .totals td{border:none;padding:5px 10px;font-size:13px}
      .totals tr.grand-total{font-weight:bold;border-top:2px solid ${accent}}
      .notes-section{margin-top:30px;clear:both;font-size:13px}
      .notes-section strong{display:block;margin-bottom:4px}
      .signatures{clear:both;margin-top:120px;padding-top:20px;display:flex;justify-content:space-between;gap:40px}
      .sig-block{text-align:center;width:200px;padding-top:50px}
      .sig-line{border-top:1px solid #333;padding-top:5px;font-size:13px}
      .footer-text{margin-top:40px;clear:both;text-align:center;font-size:11px;color:#333;border-top:1px solid #eee;padding-top:10px}
    `,
    modern: `${base}
      .print-page{margin:0}
      .header{background:${accent};color:white;padding:30px 40px;display:flex;justify-content:space-between;align-items:center}
      .header-left p{margin:2px 0} .header-right{text-align:right}
      .company-name{font-weight:700;font-size:20px;color:white}
      .company-detail{font-size:12px;color:rgba(255,255,255,0.85)}
      .company-tagline{color:rgba(255,255,255,0.92)}
      .doc-title{color:white;font-size:24px;font-weight:700;margin:0}
      .doc-number{font-size:18px;font-weight:600;margin:4px 0 0 0;color:rgba(255,255,255,0.9)}
      .doc-meta{font-size:13px;margin:2px 0;color:rgba(255,255,255,0.85)}
      .body-content{padding:30px 40px}
      .info-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:15px;margin-bottom:25px;background:#f8f8f8;border-radius:8px;padding:20px}
      .info-item label{font-weight:600;font-size:11px;color:#1A1A1A;display:block;text-transform:uppercase;letter-spacing:0.5px}
      .info-item span{font-size:14px;color:#333}
      table.items{width:100%;border-collapse:collapse;margin-bottom:20px}
      table.items th{background:#f1f1f1;color:#333;padding:10px 8px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;border-bottom:2px solid #ddd}
      table.items td{padding:10px 8px;border-bottom:1px solid #eee;font-size:13px}
      table.items tr:hover{background:#fafafa}
      .totals{float:right;width:320px}
      .totals table{width:100%;margin-top:10px;background:#f8f8f8;border-radius:8px;overflow:hidden}
      .totals td{border:none;padding:8px 15px;font-size:13px}
      .totals tr.grand-total{font-weight:bold;background:${accent};color:white}
      .notes-section{margin-top:30px;clear:both;font-size:13px;background:#f8f8f8;border-radius:8px;padding:15px}
      .notes-section strong{display:block;margin-bottom:4px;color:${accent}}
      .signatures{clear:both;margin-top:120px;padding-top:20px;display:flex;justify-content:space-between;gap:40px}
      .sig-block{text-align:center;width:200px;padding-top:50px}
      .sig-line{border-top:2px solid ${accent};padding-top:8px;font-size:12px;color:#333}
      .footer-text{margin-top:40px;clear:both;text-align:center;font-size:11px;color:#333;border-top:1px solid #eee;padding-top:10px}
    `,
    detailed: `${base}
      .print-page{margin:40px}
      .header{display:flex;justify-content:space-between;margin-bottom:25px;border:2px solid ${accent};padding:20px}
      .header-left p{margin:2px 0} .header-right{text-align:right}
      .company-name{font-weight:700;font-size:18px;color:${accent}}
      .company-detail{font-size:12px;color:#333}
      .doc-title{color:${accent};font-size:22px;font-weight:700;margin:0}
      .doc-number{font-size:18px;font-weight:600;margin:4px 0 0 0}
      .doc-meta{font-size:13px;margin:2px 0}
      .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:0;margin-bottom:25px;border:1px solid #ccc}
      .info-item{padding:10px 15px;border-bottom:1px solid #eee;border-right:1px solid #eee}
      .info-item label{font-weight:600;font-size:11px;color:#1A1A1A;display:block;text-transform:uppercase;letter-spacing:0.5px}
      .info-item span{font-size:14px}
      table.items{width:100%;border-collapse:collapse;margin-bottom:20px;border:1px solid #ccc}
      table.items th{background:${accent};color:white;padding:10px 8px;text-align:left;font-size:12px;border:1px solid ${accent}}
      table.items td{padding:8px;border:1px solid #ddd;font-size:13px}
      table.items tr:nth-child(even){background:#f9f9f9}
      .totals{float:right;width:340px;border:1px solid #ccc}
      .totals table{width:100%;margin:0}
      .totals td{border:none;padding:8px 12px;font-size:13px;border-bottom:1px solid #eee}
      .totals tr.grand-total{font-weight:bold;background:${accent}15;border-top:2px double ${accent}}
      .notes-section{margin-top:30px;clear:both;font-size:13px;border:1px solid #ddd;padding:15px}
      .notes-section strong{display:block;margin-bottom:4px}
      .signatures{clear:both;margin-top:120px;padding-top:20px;display:flex;justify-content:space-between;gap:40px}
      .sig-block{text-align:center;width:200px;border:1px solid #ddd;padding:40px 10px 10px}
      .sig-line{border-top:1px solid #333;padding-top:5px;font-size:13px}
      .footer-text{margin-top:40px;clear:both;text-align:center;font-size:11px;color:#333;border-top:2px double #ccc;padding-top:10px}
    `,
    compact: `${base}
      .print-page{margin:20px}
      .header{display:flex;justify-content:space-between;margin-bottom:15px;border-bottom:1px solid ${accent};padding-bottom:10px}
      .header-left p{margin:1px 0} .header-right{text-align:right}
      .company-name{font-weight:700;font-size:15px;color:${accent}}
      .company-detail{font-size:10px;color:#333}
      .doc-title{color:${accent};font-size:18px;font-weight:700;margin:0}
      .doc-number{font-size:14px;font-weight:600;margin:2px 0 0 0}
      .doc-meta{font-size:11px;margin:1px 0}
      .info-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-bottom:15px}
      .info-item label{font-weight:600;font-size:10px;color:#1A1A1A;display:block}
      .info-item span{font-size:12px}
      table.items{width:100%;border-collapse:collapse;margin-bottom:12px}
      table.items th{background:${accent};color:white;padding:5px 6px;text-align:left;font-size:10px}
      table.items td{padding:4px 6px;border-bottom:1px solid #eee;font-size:11px}
      table.items tr:nth-child(even){background:#fafafa}
      .totals{float:right;width:280px}
      .totals table{width:100%;margin-top:5px}
      .totals td{border:none;padding:3px 8px;font-size:11px}
      .totals tr.grand-total{font-weight:bold;border-top:1px solid ${accent}}
      .notes-section{margin-top:15px;clear:both;font-size:11px}
      .notes-section strong{display:block;margin-bottom:2px}
      .signatures{clear:both;margin-top:80px;padding-top:20px;display:flex;justify-content:space-between;gap:30px}
      .sig-block{text-align:center;width:160px;padding-top:30px}
      .sig-line{border-top:1px solid #333;padding-top:4px;font-size:10px}
      .footer-text{margin-top:20px;clear:both;text-align:center;font-size:9px;color:#333;border-top:1px solid #eee;padding-top:6px}
    `,
  };

  return templates[template] || templates.classic;
}

// ─── Build Header ─────────────────────────────────────────────────────────────

function buildHeader(docTypeKey, data, activeCompany, config, activeBranch) {
  const meta = DOC_META[docTypeKey];
  const c = activeCompany || {};

  // Use branch details if branch is active and has a specific branch selected (not "All")
  const hasBranch = activeBranch && activeBranch.id && activeBranch.id !== 'all';
  const b = hasBranch ? activeBranch : null;

  // Right side (doc title, number, date, status) — always rendered regardless of letterhead.
  const rightLines = [];
  rightLines.push(`<h1 class="doc-title">${config.customTitle ? escHtml(config.customTitle) : meta.title}</h1>`);
  rightLines.push(`<p class="doc-number">${data.docNumber}</p>`);
  rightLines.push(`<p class="doc-meta">Date: ${fmtIfDate(data.docDate)}</p>`);
  if (meta.extraHeaderFields) {
    meta.extraHeaderFields(data).forEach(f => {
      if (f.value) rightLines.push(`<p class="doc-meta">${f.label}: ${f.value}</p>`);
    });
  }
  rightLines.push(`<p class="doc-meta">Status: ${data.status}</p>`);

  // Invoice Letterhead Choice → Blank: no company details (no name, address,
  // logo); the document title / number / date block stays.
  if (config.altLetterhead && config.altLetterhead.kind === 'blank') {
    return `<div class="header">
    <div class="header-left"></div>
    <div class="header-right">${rightLines.join('')}</div>
  </div>`;
  }
  // Invoice Letterhead Choice → another name: exactly the details saved for it.
  if (config.altLetterhead && config.altLetterhead.kind === 'custom') {
    const a = config.altLetterhead;
    const lines = [`<p class="company-name">${escHtml(a.name || '')}</p>`];
    if (a.address) lines.push(`<p class="company-detail">${escHtml(a.address)}</p>`);
    if (a.phone) lines.push(`<p class="company-detail">${escHtml(a.phone)}</p>`);
    if (a.email) lines.push(`<p class="company-detail">${escHtml(a.email)}</p>`);
    const isVatCo = (c.taxLabel || 'GST').toUpperCase() === 'VAT';
    const ids = [];
    if (a.ntn) ids.push((isVatCo ? 'CR' : 'NTN') + ': ' + escHtml(a.ntn));
    if (a.salesTaxNo) ids.push(escHtml(c.taxLabel || 'GST') + ': ' + escHtml(a.salesTaxNo));
    if (ids.length) lines.push(`<p class="company-detail">${ids.join(' | ')}</p>`);
    return `<div class="header">
    <div class="header-left">${lines.join('')}</div>
    <div class="header-right">${rightLines.join('')}</div>
  </div>`;
  }

  // Letterhead header mode: when the user's header IMAGE is uploaded for this doc,
  // it already carries the company/branch identity (logo, name, NTN, GST etc.) so we
  // suppress the built-in left block entirely. The doc title/number block on the
  // right still renders so the printed page identifies which document it is.
  // If only watermark or only footer is uploaded, the built-in header still shows —
  // each of the three letterhead layers is independent, by design.
  if (config.letterhead && config.letterhead.headerUrl) {
    return `<div class="header lh-doc-info" style="border:none;justify-content:flex-end;padding-bottom:0;margin-bottom:15px">
      <div class="header-right">${rightLines.join('')}</div>
    </div>`;
  }

  const companyLines = [];
  // Show branch name as primary, with company name as subtitle — or just company name
  // (also when Print Settings → Show Branch Name is switched off).
  if (b && config.showBranchName !== false) {
    companyLines.push(`<p class="company-name">${b.branchName || c.companyName || 'Company'}</p>`);
    companyLines.push(`<p class="company-detail" style="font-weight:500">${c.companyName || ''}</p>`);
  } else {
    companyLines.push(`<p class="company-name">${c.companyName || 'Company'}</p>`);
  }
  // Trade Name as a tagline directly under the company name (opt-in per company).
  if (config.showTradeName && c.tradeName) {
    companyLines.push(`<p class="company-tagline">${c.tradeName}</p>`);
  }

  // Address: branch address if available, else company address
  const displayAddr = b && b.address ? [b.address, b.city || c.city].filter(Boolean).join(', ')
    : [c.address, c.city].filter(Boolean).join(', ');
  if (displayAddr) companyLines.push(`<p class="company-detail">${displayAddr}</p>`);

  // Phone: branch phone if available, else company phone
  if (config.showCompanyPhone) {
    const displayPhone = b && b.phone ? 'Ph: ' + b.phone : '';
    const companyPhones = [c.phone ? 'Ph: ' + c.phone : '', c.mobile ? 'Mob: ' + c.mobile : ''].filter(Boolean).join(' | ');
    const phones = (b && b.phone) ? displayPhone : companyPhones;
    if (phones) companyLines.push(`<p class="company-detail">${phones}</p>`);
  }

  // Email: branch email if available, else company email
  if (config.showCompanyEmail) {
    const displayEmail = (b && b.email) ? b.email : c.email;
    if (displayEmail) companyLines.push(`<p class="company-detail">${displayEmail}</p>`);
  }

  // Tax IDs: always from company (NTN/GST are company-level, not branch-level).
  // VAT mode swaps "NTN" → "CR" for the company's registration number.
  const isVat = (c.taxLabel || 'GST').toUpperCase() === 'VAT';
  const regLabel = isVat ? 'CR' : 'NTN';
  const taxIds = [];
  if (config.showCompanyNTN && c.ntn) taxIds.push(regLabel + ': ' + c.ntn);
  if (config.showCompanyGST && c.gstNo) taxIds.push((c.taxLabel || 'GST') + ': ' + c.gstNo);
  if (taxIds.length) companyLines.push(`<p class="company-detail">${taxIds.join(' | ')}</p>`);

  // Logo placement
  const logoAlign = config.logoPosition || 'left';
  let logoHtml = '';
  const logoSrc = c.logo || c.logoUrl;
  if (logoAlign !== 'hidden' && logoSrc) {
    const alignStyle = logoAlign === 'center' ? 'text-align:center;width:100%;margin-bottom:10px' : '';
    logoHtml = `<img src="${logoSrc}" style="max-height:60px;max-width:180px;${alignStyle}" alt="Logo" />`;
  }

  if (logoAlign === 'center' && logoHtml) {
    return `<div style="text-align:center;margin-bottom:10px">${logoHtml}</div>
      <div class="header">
        <div class="header-left">${companyLines.join('')}</div>
        <div class="header-right">${rightLines.join('')}</div>
      </div>`;
  }

  const leftContent = logoAlign === 'left' && logoHtml
    ? `<div style="margin-bottom:8px">${logoHtml}</div>${companyLines.join('')}`
    : companyLines.join('');

  const rightContent = logoAlign === 'right' && logoHtml
    ? `${rightLines.join('')}<div style="margin-top:8px">${logoHtml}</div>`
    : rightLines.join('');

  return `<div class="header">
    <div class="header-left">${leftContent}</div>
    <div class="header-right">${rightContent}</div>
  </div>`;
}

// ─── Build Info Grid ──────────────────────────────────────────────────────────

function buildInfoGrid(docTypeKey, data, activeCompany, config = {}) {
  const meta = DOC_META[docTypeKey];
  const fields = meta.infoFields(data);
  // Swap tax-related labels based on the company's taxLabel setting so VAT
  // companies print "CR / VAT" instead of "NTN / STRN / GST".
  const isVat = (activeCompany?.taxLabel || 'GST').toUpperCase() === 'VAT';
  const swapLabel = (label) => {
    if (!isVat) return label;
    if (label === 'NTN') return 'CR';
    if (label === 'STRN') return 'VAT #';
    return label;
  };
  // Drop fields explicitly marked hideIfEmpty when their value is blank.
  // Other fields still render with '-' for back-compat.
  const items = fields
    // Per-document field toggles (Sale Invoice). A field with no cfgKey, or a
    // company that hasn't turned it off, prints exactly as before.
    .filter(f => !f.cfgKey || config[f.cfgKey] !== false)
    .filter(f => !(f.hideIfEmpty && (f.value === undefined || f.value === null || String(f.value).trim() === '')))
    .map(f => `<div class="info-item"><label>${swapLabel(f.label)}</label><span>${fmtIfDate(f.value) || '-'}</span></div>`)
    .join('');
  return `<div class="info-grid">${items}</div>`;
}

// ─── Build Table ──────────────────────────────────────────────────────────────

function buildTable(docTypeKey, data, config, baseCurrency = 'PKR') {
  const meta = DOC_META[docTypeKey];
  const lines = data.lines || [];
  const cols = config.columns || {};

  if (meta.columnSet === 'grn') return buildGRNTable(lines);
  if (meta.columnSet === 'dc') return buildDCTable(lines);
  if (meta.columnSet === 'return') return buildReturnTable(lines, cols, docTypeKey);
  if (meta.columnSet === 'stockAdjust') return buildStockAdjustTable(lines);
  if (meta.columnSet === 'requisition') return buildRequisitionTable(lines);
  if (meta.columnSet === 'gatePass') return buildGatePassTable(lines);
  if (meta.columnSet === 'bom') return buildBOMTable(lines);
  if (meta.columnSet === 'production') return buildProductionTable(lines);
  if (meta.columnSet === 'import') return buildImportTable(lines, data, baseCurrency);
  if (meta.columnSet === 'payslip') return buildPayslipBreakdown(data, config.accentColor, baseCurrency);
  if (meta.columnSet === 'voucher') return buildVoucherExtras(data, config, baseCurrency);
  if (meta.columnSet === 'loanRecovery') return buildLoanRecoveryTable(data, config, baseCurrency);
  if (meta.columnSet === 'journal') return buildJournalTable(data);
  // Quotation only, off unless the company switches it on: split the lines into
  // a Stores table and a Services table, each with its own sub-total and tax.
  if (docTypeKey === 'quotation' && config.splitStoresServices) {
    return buildSplitStoresServicesTables(lines, cols, docTypeKey, data, baseCurrency);
  }
  // Sale Invoice with 3rd Schedule items (company has 3rd Schedule Sales Tax on):
  // Retail / Trade Price columns instead of Rate / Discount (see buildRetailTradeTable).
  if (config.retailTradeLayout) return buildRetailTradeTable(lines, cols, docTypeKey);
  // Standard columns
  return buildStandardTable(lines, cols, docTypeKey);
}

// ─── Retail / Trade Price layout (2026-10-29) ────────────────────────────────
// Automatic on the Sale Invoice print when the company has "3rd Schedule Sales
// Tax" on AND the invoice has at least one 3rd Schedule item. Tax on those items
// is worked out on the retail price (the unit price), the customer pays the
// trade price (retail less the line discount) plus that tax. Columns:
//   Unit Price (Retail) | Amount Excl. Tax (qty × retail) | Tax % | Sales Tax |
//   Trade Price (per unit) | Total Trade Price | Value Incl. Tax
// Only the printed columns change — every figure is the one saved on the invoice.
// A normal item on the same invoice keeps its own tax (on the trade price).
export const isRetailTradeLayout = (docTypeKey, data, activeCompany) =>
  docTypeKey === 'saleInvoice'
  && isModuleEnabledForCompany('sales_third_schedule_tax', activeCompany?.enabledModules)
  && (data?.lines || []).some(l => l && l.thirdSchedule === true)
  && !(data?.lines || []).some(l => l && l.hasSize);

function buildRetailTradeTable(lines, cols, docTypeKey) {
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const showUom = cols.uom !== false;
  const showImage = cols.image !== false;
  const showHs = cols.hsCode !== false && lines.some(l => l && l.hsCode);
  const showBarcode = cols.barcode === true && lines.some(l => l && l.barcode);
  const showSrc = cols.sourceDoc !== false && lines.some(l => l && l.sourceDocNumber);
  const srcLabel = lines.some(l => l && l.sourceDocNumber && /^GRN/i.test(String(l.sourceDocNumber))) ? 'GRN' : 'Ref';
  const showMrp = cols.mrp !== false && lines.some(l => l && Number(l.mrp) > 0);
  const taxed = lines.filter(l => l && l.taxable);
  const allRetailTax = taxed.length > 0 && taxed.every(l => l.thirdSchedule === true);
  const R = 'style="text-align:right"';

  let head = '<th>#</th>';
  if (showSrc) head += `<th>${srcLabel} #</th><th>${srcLabel} Date</th>`;
  head += '<th>Description</th>';
  if (showHs) head += '<th>HS Code</th>';
  if (showBarcode) head += '<th>Barcode</th>';
  head += `<th ${R}>Qty</th>`;
  if (showUom) head += '<th>Unit</th>';
  head += `<th ${R}>Unit Price (Retail)</th><th ${R}>Amount Excl. Tax</th>`;
  head += `<th ${R}>Tax %</th><th ${R}>${allRetailTax ? 'Sales Tax on Retail Price' : 'Sales Tax'}</th>`;
  head += `<th ${R}>Trade Price</th><th ${R}>Total Trade Price</th>`;
  if (showMrp) head += `<th ${R}>MRP</th>`;
  head += `<th ${R}>Value Incl. Tax</th>`;

  const sums = { qty: 0, gross: 0, tax: 0, trade: 0, mrp: 0, value: 0 };
  const rows = lines.map((l, i) => {
    const qty = Number(l.quantity) || 0;
    const gross = r2(qty * (Number(l.unitPrice) || 0));
    const trade = r2(gross - (Number(l.discountAmount) || 0));
    const tax = l.taxable ? (Number(l.taxAmount) || 0) : 0;
    const mrp = Number(l.mrp) || 0;
    const value = Number(l.lineTotal) || 0;
    sums.qty += qty; sums.gross += gross; sums.tax += tax; sums.trade += trade; sums.mrp += mrp; sums.value += value;
    const taxPct = Number(l.taxRate) || 0;
    let row = `<td>${i + 1}</td>`;
    if (showSrc) row += `<td>${l.sourceDocNumber || '-'}</td><td>${fmtIfDate(l.sourceDocDate) || '-'}</td>`;
    row += `<td>${showImage ? itemThumb(l) : ''}${l.description || ''}</td>`;
    if (showHs) row += `<td>${l.hsCode || ''}</td>`;
    if (showBarcode) row += `<td style="white-space:nowrap">${l.barcode || ''}</td>`;
    row += `<td ${R}>${qtyCell(l, { unitShown: !!showUom })}</td>`;
    if (showUom) row += `<td>${l.unitOfMeasure || ''}</td>`;
    row += `<td ${R}>${formatAmount(l.unitPrice)}</td><td ${R}>${formatAmount(gross)}</td>`;
    row += `<td ${R}>${l.taxable && taxPct ? taxPct + '%' : '-'}</td><td ${R}>${l.taxable ? formatAmount(tax) : '-'}</td>`;
    row += `<td ${R}>${qty ? formatAmount(trade / qty) : '-'}</td><td ${R}>${formatAmount(trade)}</td>`;
    if (showMrp) row += `<td ${R}>${mrp ? formatAmount(mrp) : '-'}</td>`;
    row += `<td ${R}>${formatAmount(value)}</td>`;
    return `<tr>${row}</tr>`;
  }).join('');

  let footRows = '';
  if (lines.length > 0) {
    const fb = 'font-weight:bold';
    let lead = 2 + (showSrc ? 2 : 0) + (showHs ? 1 : 0) + (showBarcode ? 1 : 0);   // #, Description, …
    let f = `<td colspan="${lead}" style="text-align:right;${fb}">Total</td>`;
    f += `<td style="text-align:right;${fb}">${sums.qty.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>`;
    if (showUom) f += '<td></td>';
    f += `<td></td><td style="text-align:right;${fb}">${formatAmount(r2(sums.gross))}</td>`;
    f += `<td></td><td style="text-align:right;${fb}">${formatAmount(r2(sums.tax))}</td>`;
    f += `<td></td><td style="text-align:right;${fb}">${formatAmount(r2(sums.trade))}</td>`;
    if (showMrp) f += `<td style="text-align:right;${fb}">${sums.mrp ? formatAmount(r2(sums.mrp)) : '-'}</td>`;
    f += `<td style="text-align:right;${fb}">${formatAmount(r2(sums.value))}</td>`;
    footRows = `<tr style="border-top:2px solid #999">${f}</tr>`;
  }
  // Total row printed once, after the last item (not repeated on every page).
  const footer = footRows ? `<tfoot style="display:table-row-group">${footRows}</tfoot>` : '';
  return `<table class="items"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody>${footer}</table>`;
}

// ─── Voucher extras — multi-party / petty / invoice-allocation tables ───────
// Single-party CR/BR/CP/BP and ADV/LON carry none of these, so nothing renders
// (info grid + total only, as before). When a payment/receipt has partyLines
// (multi-party), contraLines (petty), or invoiceAllocations, we render the same
// detail tables the old inline prints did — now inside the settings-driven template.
// Bank / Cash RECEIPT helpers. A receipt's gross (amount cleared from the
// customer) = cash received + WHT + adjustment / bad debt. It is DERIVED, never
// read from `grossAmount` — that field is a subtotal alias the save code
// overwrites (always 0 on receipts AND payments).
function isReceiptDoc(d) {
  return !!d && (d.tranTypeCode === 'CR' || d.tranTypeCode === 'BR');
}
function receiptGrossOf(d) {
  const base = Number(d?.totalAmount) || Number(d?.amount) || 0;
  return Math.round((base + (Number(d?.whtAmount) || 0) + (Number(d?.adjustmentAmount) || 0)) * 100) / 100;
}

// Loan Recovery receipt: which loans / advances the money went against, with the
// balance before and after this receipt.
function buildLoanRecoveryTable(data, config, currency = 'PKR') {
  const accent = config.accentColor || '#800020';
  const th = 'text-align:left;padding:6px 10px;border:1px solid #ddd';
  const thr = 'text-align:right;padding:6px 10px;border:1px solid #ddd';
  const td = 'padding:6px 10px;border:1px solid #ddd';
  const tdr = 'text-align:right;padding:6px 10px;border:1px solid #ddd';
  const rows = (data.allocations || []).filter(a => Number(a.amount) > 0);
  if (rows.length === 0) return '';
  const kind = (c) => (c === 'ADV' ? 'Salary Advance' : 'Staff Loan');
  const after = (a) => Math.max(0, Math.round(((Number(a.balanceBefore) || 0) - (Number(a.amount) || 0)) * 100) / 100);
  return `<table style="width:100%;border-collapse:collapse;font-size:13px;margin-top:10px">
    <tr style="background:#f5f5f5"><th style="${th}">#</th><th style="${th}">Loan / Advance</th><th style="${th}">Type</th><th style="${thr}">Balance Before</th><th style="${thr}">Received (${escHtml(currency)})</th><th style="${thr}">Balance After</th></tr>
    ${rows.map((a, i) => `<tr><td style="${td}">${i + 1}</td><td style="${td}">${escHtml(a.docNumber || '')}</td><td style="${td}">${kind(a.tranTypeCode)}</td><td style="${tdr}">${formatAmount(a.balanceBefore || 0)}</td><td style="${tdr}">${formatAmount(a.amount)}</td><td style="${tdr}">${formatAmount(after(a))}</td></tr>`).join('')}
    <tr style="font-weight:bold;border-top:2px solid ${accent}"><td colspan="4" style="${td}">Total</td><td style="${tdr}">${formatAmount(data.totalAmount)}</td><td style="${td}"></td></tr>
  </table>`;
}

function buildVoucherExtras(data, config, currency = 'PKR') {
  const accent = config.accentColor || '#800020';
  const th = 'text-align:left;padding:6px 10px;border:1px solid #ddd';
  const thr = 'text-align:right;padding:6px 10px;border:1px solid #ddd';
  const td = 'padding:6px 10px;border:1px solid #ddd';
  const tdr = 'text-align:right;padding:6px 10px;border:1px solid #ddd';
  let out = '';

  const partyLines = Array.isArray(data.partyLines) ? data.partyLines : [];
  // Multi-party Bank/Cash RECEIPT with per-invoice WHT / write-off: Amount =
  // gross cleared, then WHT / Bad Debt / Net per customer. (Keyed on the doc
  // type — payments keep their own WHT table below.)
  if (partyLines.length > 0 && isReceiptDoc(data) &&
      (Number(data.whtAmount) > 0 || Number(data.adjustmentAmount) > 0)) {
    out += `<h3 style="margin:20px 0 10px;font-size:14px;color:${accent}">Party Lines</h3>
      <table style="width:100%;border-collapse:collapse;font-size:13px">
        <tr style="background:#f5f5f5"><th style="${th}">#</th><th style="${th}">Party</th><th style="${thr}">Amount (${currency})</th><th style="${thr}">WHT</th><th style="${thr}">Bad Debt / Adj.</th><th style="${thr}">Net Received</th></tr>
        ${partyLines.map((pl, i) => `<tr><td style="${td}">${i + 1}</td><td style="${td}">${pl.partyName || '-'}</td><td style="${tdr}">${formatAmount(pl.amount)}</td><td style="${tdr}">${formatAmount(pl.whtAmount || 0)}</td><td style="${tdr}">${formatAmount(pl.adjustmentAmount || 0)}</td><td style="${tdr}">${formatAmount(pl.netAmount ?? pl.amount)}</td></tr>`).join('')}
        <tr style="font-weight:bold;border-top:2px solid ${accent}"><td colspan="2" style="${td}">Total</td><td style="${tdr}">${formatAmount(receiptGrossOf(data))}</td><td style="${tdr}">${formatAmount(data.whtAmount || 0)}</td><td style="${tdr}">${formatAmount(data.adjustmentAmount || 0)}</td><td style="${tdr}">${formatAmount(data.totalAmount)}</td></tr>
      </table>`;
  } else if (partyLines.length > 0) {
    // Payments (and plain multi-party receipts). Net is DERIVED — the stored
    // doc-level netAmount is overwritten with the gross by the save code.
    const mpAdj = Number(data.adjustmentAmount) || 0;
    const lineNet = (pl) => Math.round(((Number(pl.amount) || 0) - (Number(pl.whtAmount) || 0) - (Number(pl.adjustmentAmount) || 0)) * 100) / 100;
    const docNet = isReceiptDoc(data)
      ? (Number(data.totalAmount) || 0)
      : Math.round(((Number(data.totalAmount) || 0) - (Number(data.whtAmount) || 0) - mpAdj) * 100) / 100;
    out += `<h3 style="margin:20px 0 10px;font-size:14px;color:${accent}">Party Lines</h3>
      <table style="width:100%;border-collapse:collapse;font-size:13px">
        <tr style="background:#f5f5f5"><th style="${th}">#</th><th style="${th}">Party</th><th style="${thr}">Amount (${currency})</th><th style="${thr}">WHT%</th><th style="${thr}">WHT Amt</th>${mpAdj > 0 ? `<th style="${thr}">Adjustment</th>` : ''}<th style="${thr}">Net</th></tr>
        ${partyLines.map((pl, i) => `<tr><td style="${td}">${i + 1}</td><td style="${td}">${pl.partyName || '-'}</td><td style="${tdr}">${formatAmount(pl.amount)}</td><td style="${tdr}">${pl.whtRate || 0}%</td><td style="${tdr}">${formatAmount(pl.whtAmount || 0)}</td>${mpAdj > 0 ? `<td style="${tdr}">${formatAmount(pl.adjustmentAmount || 0)}</td>` : ''}<td style="${tdr}">${formatAmount(lineNet(pl))}</td></tr>`).join('')}
        <tr style="font-weight:bold;border-top:2px solid ${accent}"><td colspan="2" style="${td}">Total</td><td style="${tdr}">${formatAmount(data.totalAmount)}</td><td style="${td}"></td><td style="${tdr}">${formatAmount(data.whtAmount || 0)}</td>${mpAdj > 0 ? `<td style="${tdr}">${formatAmount(mpAdj)}</td>` : ''}<td style="${tdr}">${formatAmount(docNet)}</td></tr>
      </table>`;
  }

  const contraLines = Array.isArray(data.contraLines) ? data.contraLines : [];
  if (contraLines.length > 0) {
    out += `<h3 style="margin:20px 0 10px;font-size:14px;color:${accent}">Contra Accounts</h3>
      <table style="width:100%;border-collapse:collapse;font-size:13px">
        <tr style="background:#f5f5f5"><th style="${th}">#</th><th style="${th}">Account</th><th style="${th}">Description</th><th style="${thr}">Amount (${currency})</th></tr>
        ${contraLines.map((cl, i) => `<tr><td style="${td}">${i + 1}</td><td style="${td}">${cl.accountCode ? cl.accountCode + ' - ' : ''}${cl.accountName || '-'}</td><td style="${td}">${cl.description || '-'}</td><td style="${tdr}">${formatAmount(cl.amount)}</td></tr>`).join('')}
        <tr style="font-weight:bold;border-top:2px solid ${accent}"><td colspan="3" style="${td}">Total</td><td style="${tdr}">${formatAmount(data.totalAmount)}</td></tr>
      </table>`;
  }

  const allocs = Array.isArray(data.invoiceAllocations) ? data.invoiceAllocations : [];
  if (allocs.length > 0) {
    out += `<h3 style="margin:20px 0 10px;font-size:14px;color:${accent}">Invoice Allocations</h3>
      <table style="width:100%;border-collapse:collapse;font-size:13px">
        <tr style="background:#f5f5f5"><th style="${th}">Invoice</th><th style="${thr}">Allocated (${currency})</th></tr>
        ${allocs.map(a => `<tr><td style="${td}">${a.docNumber}</td><td style="${tdr}">${formatAmount(a.allocatedAmount)}</td></tr>`).join('')}
      </table>`;
  }

  return out;
}

// ─── Journal Voucher — GL lines with Dr / Cr columns ────────────────────────
function buildJournalTable(data) {
  const entries = data.journalEntries || data.entries || data.subTransactions || data.lines || [];
  const fmt = (n) => {
    const v = Number(n) || 0;
    return v > 0 ? v.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
  };
  let totalDr = 0, totalCr = 0;
  const rows = entries.map((e, i) => {
    const dr = Number(e.debitAmount ?? e.dr ?? e.debit) || 0;
    const cr = Number(e.creditAmount ?? e.cr ?? e.credit) || 0;
    totalDr += dr; totalCr += cr;
    const accountLabel = [e.accountCode, e.accountName || e.account]
      .filter(Boolean).join(' - ');
    return `<tr>
      <td style="padding:6px 8px;border:1px solid #ddd;text-align:center">${i + 1}</td>
      <td style="padding:6px 8px;border:1px solid #ddd">${accountLabel}</td>
      <td style="padding:6px 8px;border:1px solid #ddd">${e.description || e.narration || ''}</td>
      <td style="padding:6px 8px;border:1px solid #ddd;text-align:right">${fmt(dr)}</td>
      <td style="padding:6px 8px;border:1px solid #ddd;text-align:right">${fmt(cr)}</td>
    </tr>`;
  }).join('');
  return `
    <table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:12px">
      <thead style="background:#f5f5f5">
        <tr>
          <th style="padding:8px;border:1px solid #ddd;text-align:center">#</th>
          <th style="padding:8px;border:1px solid #ddd;text-align:left">Account</th>
          <th style="padding:8px;border:1px solid #ddd;text-align:left">Description</th>
          <th style="padding:8px;border:1px solid #ddd;text-align:right">Debit</th>
          <th style="padding:8px;border:1px solid #ddd;text-align:right">Credit</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
      <tfoot>
        <tr style="font-weight:bold;background:#fafafa">
          <td colspan="3" style="padding:8px;border:1px solid #ddd;text-align:right">Total</td>
          <td style="padding:8px;border:1px solid #ddd;text-align:right">${fmt(totalDr)}</td>
          <td style="padding:8px;border:1px solid #ddd;text-align:right">${fmt(totalCr)}</td>
        </tr>
      </tfoot>
    </table>
  `;
}

// ─── Payslip — Earnings + Deductions side-by-side ────────────────────────────

function buildPayslipBreakdown(data, accent, baseCurrency = 'PKR') {
  const l = data.line || {};
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const fmt = (n) => Number(r2(n)).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Earnings rows
  const earningsRows = [];
  earningsRows.push(`<tr><td>Basic Salary (prorated)</td><td style="text-align:right">${fmt(l.basicProrated)}</td></tr>`);
  (l.allowances || []).forEach(a => {
    earningsRows.push(`<tr><td>${a.name}${a.amountType === 'percent' ? ` (${a.value}% of basic)` : ''}</td><td style="text-align:right">${fmt(a.prorated)}</td></tr>`);
  });
  if (Number(l.bonus) > 0) earningsRows.push(`<tr><td>Bonus</td><td style="text-align:right">${fmt(l.bonus)}</td></tr>`);
  if (Number(l.arrears) > 0) earningsRows.push(`<tr><td>Arrears</td><td style="text-align:right">${fmt(l.arrears)}</td></tr>`);
  if (Number(l.overtime) > 0) earningsRows.push(`<tr><td>Overtime</td><td style="text-align:right">${fmt(l.overtime)}</td></tr>`);
  if (Number(l.leaveDeduction) > 0) earningsRows.push(`<tr><td>− Leave Cut</td><td style="text-align:right;color:red">−${fmt(l.leaveDeduction)}</td></tr>`);

  // Deductions rows
  const deductionsRows = [];
  (l.deductions || []).forEach(d => {
    const amt = Number(d.computed ?? d.value) || 0;
    if (amt > 0) deductionsRows.push(`<tr><td>${d.name}</td><td style="text-align:right">${fmt(amt)}</td></tr>`);
  });
  if (Number(l.loanInstallment) > 0) deductionsRows.push(`<tr><td>Loan Installment</td><td style="text-align:right">${fmt(l.loanInstallment)}</td></tr>`);
  if (Number(l.advanceRecovery) > 0) deductionsRows.push(`<tr><td>Advance Recovery</td><td style="text-align:right">${fmt(l.advanceRecovery)}</td></tr>`);

  return `
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:20px">
      <div>
        <h3 style="color:${accent};font-size:14px;margin-bottom:8px;border-bottom:1px solid ${accent};padding-bottom:4px">Earnings</h3>
        <table style="width:100%;border-collapse:collapse;font-size:12px">
          <tbody>${earningsRows.join('')}</tbody>
          <tfoot><tr style="border-top:2px solid ${accent};font-weight:bold;background:${accent}10">
            <td style="padding:8px 6px">Adjusted Gross</td>
            <td style="text-align:right;padding:8px 6px">${fmt(l.adjustedGross)}</td>
          </tr></tfoot>
        </table>
      </div>
      <div>
        <h3 style="color:${accent};font-size:14px;margin-bottom:8px;border-bottom:1px solid ${accent};padding-bottom:4px">Deductions</h3>
        <table style="width:100%;border-collapse:collapse;font-size:12px">
          <tbody>${deductionsRows.length > 0 ? deductionsRows.join('') : '<tr><td colspan="2" style="text-align:center;color:#999;padding:8px">No deductions</td></tr>'}</tbody>
          <tfoot><tr style="border-top:2px solid ${accent};font-weight:bold;background:${accent}10">
            <td style="padding:8px 6px">Total Deductions</td>
            <td style="text-align:right;padding:8px 6px">${fmt(l.totalDeductions)}</td>
          </tr></tfoot>
        </table>
      </div>
    </div>
    <table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:6px">
      <tbody>
        <tr><td style="padding:4px 6px">Adjusted Gross</td><td style="text-align:right;padding:4px 6px">${fmt(l.adjustedGross)}</td></tr>
        <tr><td style="padding:4px 6px">− Total Deductions</td><td style="text-align:right;padding:4px 6px;color:red">−${fmt(l.totalDeductions)}</td></tr>
      </tbody>
    </table>
  `;
}

// ─── Import Purchase Invoice — line table + charges + landed summary ─────────

function buildImportTable(lines, data, baseCurrency = 'PKR') {
  const fc = data.foreignCurrency || 'USD';
  const showSize = (lines || []).some(l => l && l.hasSize);
  const head = `<th>#</th><th>Description</th>` +
    (showSize ? `<th>Size</th><th style="text-align:right">Pieces</th>` : '') +
    `<th>Unit</th>` +
    `<th style="text-align:right">Qty</th>` +
    `<th style="text-align:right">Weight</th>` +
    `<th style="text-align:right">Unit Price (${fc})</th>` +
    `<th style="text-align:right">Foreign Total</th>` +
    `<th style="text-align:right">${baseCurrency} Total</th>` +
    `<th style="text-align:right">Landed Unit Cost</th>`;

  const rows = (lines || []).map((l, i) => {
    const name = l.itemName || l.description || '';
    const code = l.itemCode ? `${l.itemCode} - ` : '';
    const sizeCells = showSize
      ? `<td style="white-space:nowrap">${l.sizeLabel || (l.hasSize && l.length && l.height ? `${l.length}×${l.height}${l.thickness ? '×' + l.thickness : ''}` : '-')}</td><td style="text-align:right">${l.hasSize ? (l.pieces || 0) : '-'}</td>`
      : '';
    return `<tr>
      <td>${i + 1}</td>
      <td>${code}${name}</td>
      ${sizeCells}
      <td>${l.unitOfMeasure || ''}</td>
      <td style="text-align:right">${l.quantity || 0}</td>
      <td style="text-align:right">${l.weight || 0}</td>
      <td style="text-align:right">${(l.unitPriceForeign || 0).toLocaleString()}</td>
      <td style="text-align:right">${(l.foreignTotal || 0).toLocaleString()}</td>
      <td style="text-align:right">${formatAmount(l.baseTotal || 0)}</td>
      <td style="text-align:right;font-weight:bold">${formatAmount(l.landedUnitCost || 0)}</td>
    </tr>`;
  }).join('');

  return `<table class="items"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>`;
}

function buildImportChargesSection(data, accent, baseCurrency = 'PKR') {
  const charges = data.landedCharges || data.importCharges || [];
  if (!charges.length) return '';

  const head = `<th>#</th><th>Charge</th><th>Type</th><th style="text-align:right">Value</th>` +
    `<th>Base</th><th>Capitalize</th><th>Account</th><th style="text-align:right">Computed (${baseCurrency})</th>`;

  const rows = charges.map((c, i) => {
    const valDisplay = c.type === 'percentage' ? `${c.value || 0}%` : formatAmount(c.value || 0);
    const baseLabel = c.type === 'percentage'
      ? (c.base === 'cif' ? 'CIF' : c.base === 'cifPlusDuties' ? 'CIF + Prior' : 'Manual')
      : '-';
    const acctLabel = c.capitalize
      ? (c.accountName || c.accountCode || '-')
      : `Dr ${c.claimAccountName || c.claimAccountCode || '-'} / Cr ${c.accountName || c.accountCode || '-'}`;
    return `<tr>
      <td>${i + 1}</td>
      <td>${c.chargeName || ''}</td>
      <td>${c.type === 'percentage' ? '%' : 'Amount'}</td>
      <td style="text-align:right">${valDisplay}</td>
      <td>${baseLabel}</td>
      <td>${c.capitalize ? 'Yes (→ Inventory)' : 'No (claimable)'}</td>
      <td>${acctLabel}</td>
      <td style="text-align:right;font-weight:bold">${formatAmount(c.chargeAmount || 0)}</td>
    </tr>`;
  }).join('');

  return `
    <div style="margin-top:25px;margin-bottom:15px">
      <h3 style="color:${accent};font-size:14px;margin-bottom:8px;border-bottom:1px solid ${accent};padding-bottom:4px">
        Import Charges (Duties · Taxes · Freight · Clearing)
      </h3>
      <table class="items" style="margin-bottom:0"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>
    </div>
  `;
}

function buildLandedSummarySection(data, accent, baseCurrency = 'PKR') {
  const lines = data.lines || [];
  if (!lines.length) return '';

  const head = `<th>#</th><th>Item</th><th style="text-align:right">Qty</th>` +
    `<th style="text-align:right">${baseCurrency} Cost</th>` +
    `<th style="text-align:right">+ Capitalized</th>` +
    `<th style="text-align:right">= Landed Total</th>` +
    `<th style="text-align:right">Landed Unit Cost</th>`;

  const rows = lines.map((l, i) => {
    const cap = l.capitalizedAllocated ?? l.capitalizedTotal ?? 0;
    return `<tr>
      <td>${i + 1}</td>
      <td>${l.itemName || l.description || ''}</td>
      <td style="text-align:right">${l.quantity || 0}</td>
      <td style="text-align:right">${formatAmount(l.baseTotal || 0)}</td>
      <td style="text-align:right;color:${accent}">+${formatAmount(cap)}</td>
      <td style="text-align:right;font-weight:bold">${formatAmount(l.landedTotal || (l.baseTotal || 0) + cap)}</td>
      <td style="text-align:right;font-weight:bold;color:#2e7d32">${formatAmount(l.landedUnitCost || 0)}</td>
    </tr>`;
  }).join('');

  return `
    <div style="margin-top:15px;margin-bottom:15px">
      <h3 style="color:${accent};font-size:14px;margin-bottom:8px;border-bottom:1px solid ${accent};padding-bottom:4px">
        Landed Cost Summary
      </h3>
      <table class="items" style="margin-bottom:0"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>
    </div>
  `;
}

// Render the qty cell for print. If the line was entered in a non-base unit
// (conversion > 1 AND baseQuantity differs from entered quantity), append a
// small base-equivalent hint, e.g. "10 (240 Pcs)". Single-unit lines unchanged.
// Show Two Units (2026-10-10): a line saved with secondUnitCode/secondQuantity
// prints both, e.g. "10.4167 (250 Mtr)" — plus its own unit when the print has
// no Unit column ("10.4167 Ctn (250 Mtr)").
function fmtTwoQty(n) {
  const v = Number(n);
  return Number.isFinite(v) ? String(parseFloat(v.toFixed(4))) : '';
}
function qtyCell(l, opts = {}) {
  if (l && l.secondUnitCode && l.secondQuantity != null && !l.hasSize) {
    const own = opts.unitShown === false ? ` ${l.unitCode || l.unitOfMeasure || ''}` : '';
    return `${fmtTwoQty(l.quantity)}${own} <span style="font-size:10px;color:#333">(${fmtTwoQty(l.secondQuantity)} ${l.secondUnitCode})</span>`;
  }
  const qty = l?.quantity ?? '';
  const baseQty = Number(l?.baseQuantity);
  const conv = Number(l?.conversion);
  if (Number.isFinite(baseQty) && Number.isFinite(conv) && conv > 1 && baseQty !== Number(l?.quantity)) {
    // Try to find the base unit code from line.units[]; fall back to leaving it blank
    let baseUnitLabel = '';
    if (Array.isArray(l.units) && l.units.length > 0) {
      const baseRow = l.units.find(u => u.isBase) || l.units[0];
      if (baseRow?.unitCode) baseUnitLabel = ' ' + baseRow.unitCode;
    }
    return `${qty} <span style="font-size:10px;color:#333">(${baseQty}${baseUnitLabel})</span>`;
  }
  return String(qty ?? '');
}

function itemThumb(l) {
  const url = l?.imageUrl;
  if (!url) return '';
  const safe = String(url).replace(/"/g, '&quot;');
  return `<img src="${safe}" alt="" style="width:28px;height:28px;object-fit:cover;border-radius:3px;vertical-align:middle;margin-right:6px" />`;
}

function buildStandardTable(lines, cols, docTypeKey, opts = {}) {
  // Which columns appear is decided from `colSourceLines` when given, not from
  // the rows being printed. The Quotation Stores/Services split calls this twice
  // with a slice of the lines each time; deciding per slice would give the two
  // tables DIFFERENT columns (e.g. HS Code on Stores but not Services) and they
  // would not line up under each other. Defaults to `lines`, so every existing
  // caller behaves exactly as before.
  const colLines = opts.colSourceLines || lines;
  const showDisc = cols.discountPercent !== false;
  const showTax = cols.tax !== false;
  // Column toggles (Print Settings → Sale Invoice). Each is "data has it AND the
  // company hasn't switched it off", so unset behaves exactly as before.
  const showUom = cols.uom !== false;
  const showRate = cols.rate !== false;
  const showImage = cols.image !== false;
  // HS Code column: shown only if at least one line carries an HS Code.
  const showHs = cols.hsCode !== false && (colLines || []).some(l => l && l.hsCode);
  // Barcode column (Print Settings → Sale Invoice → Item Columns). Opt-in: only
  // when switched ON (=== true) AND at least one line has a barcode. Doc types
  // that never set it are unaffected. Sits immediately before Qty.
  const showBarcode = cols.barcode === true && (colLines || []).some(l => l && l.barcode);
  // Source columns appear when any line traces back to a GRN/DC (multi-source PI/SI).
  const showSrc = cols.sourceDoc !== false && (colLines || []).some(l => l && l.sourceDocNumber);
  const srcLabel = (colLines || []).some(l => l && l.sourceDocNumber && /^GRN/i.test(String(l.sourceDocNumber))) ? 'GRN' : 'Ref';
  // MRP column appears only when at least one line carries an MRP value
  // (per-company addon — invisible for every company that doesn't use it).
  const showMrp = cols.mrp !== false && (colLines || []).some(l => l && Number(l.mrp) > 0);
  // Size mode: when any line carries hasSize, show Size + Pieces columns. Qty
  // continues to render as Total SQft (line.quantity is the SQft total) and
  // Unit shows "SQft", so the rest of the table reads naturally.
  const showSize = (colLines || []).some(l => l && l.hasSize);

  let head = '<th>#</th>';
  if (showSrc) head += `<th>${srcLabel} #</th><th>${srcLabel} Date</th>`;
  head += '<th>Description</th>';
  if (showHs) head += '<th>HS Code</th>';
  if (showSize) head += '<th>Size</th><th style="text-align:right">Pieces</th>';
  if (showBarcode) head += '<th>Barcode</th>';
  head += '<th style="text-align:right">Qty</th>';
  if (showUom) head += '<th>Unit</th>';
  if (showRate) head += '<th style="text-align:right">Unit Price</th>';
  if (showDisc) head += '<th style="text-align:right">Disc %</th><th style="text-align:right">Disc Amt</th>';
  if (showTax) head += '<th style="text-align:right">Tax %</th><th style="text-align:right">Tax Amt</th>';
  if (showMrp) head += '<th style="text-align:right">MRP</th>';
  head += '<th style="text-align:right">Total</th>';

  const rows = lines.map((l, i) => {
    const discPct = Number(l.discountPercent) || 0;
    const discAmt = Number(l.discountAmount) || 0;
    const taxPct = Number(l.taxRate) || 0;
    const taxAmt = Number(l.taxAmount) || 0;
    const mrpAmt = Number(l.mrp) || 0;
    // numberFrom keeps the S.No running across the Quotation Stores/Services
    // split (Stores 1-4, Services 5-6) instead of restarting each table at 1.
    let row = `<td>${(opts.numberFrom || 0) + i + 1}</td>`;
    if (showSrc) row += `<td>${l.sourceDocNumber || '-'}</td><td>${fmtIfDate(l.sourceDocDate) || '-'}</td>`;
    row += `<td>${showImage ? itemThumb(l) : ''}${l.description || ''}</td>`;
    if (showHs) row += `<td>${l.hsCode || ''}</td>`;
    if (showSize) {
      const sizeLabel = l.sizeLabel || (l.hasSize && l.length && l.height
        ? `${l.length}×${l.height}${l.thickness ? '×' + l.thickness : ''}`
        : '-');
      row += `<td style="white-space:nowrap">${sizeLabel}</td>`;
      row += `<td style="text-align:right">${l.hasSize ? (l.pieces || 0) : '-'}</td>`;
    }
    if (showBarcode) row += `<td style="white-space:nowrap">${l.barcode || ''}</td>`;
    row += `<td style="text-align:right">${qtyCell(l, { unitShown: !!showUom })}</td>`;
    if (showUom) row += `<td>${l.unitOfMeasure || ''}</td>`;
    if (showRate) row += `<td style="text-align:right">${formatAmount(l.unitPrice)}</td>`;
    if (showDisc) {
      row += `<td style="text-align:right">${discPct ? discPct + '%' : '-'}</td>`;
      row += `<td style="text-align:right">${discAmt ? formatAmount(discAmt) : '-'}</td>`;
    }
    if (showTax) {
      row += `<td style="text-align:right">${l.taxable && taxPct ? taxPct + '%' : '-'}</td>`;
      row += `<td style="text-align:right">${l.taxable ? formatAmount(taxAmt) : '-'}</td>`;
    }
    if (showMrp) {
      row += `<td style="text-align:right">${mrpAmt ? formatAmount(mrpAmt) : '-'}</td>`;
    }
    row += `<td style="text-align:right">${formatAmount(l.lineTotal)}</td>`;
    return `<tr>${row}</tr>`;
  }).join('');

  // Sale Invoice / Non-Tax Sale Invoice: a totals row where the item table closes,
  // summing Qty and Discount (plus Tax where shown). Other doc types are unchanged.
  const showTotalsRow = docTypeKey === 'saleInvoice' || docTypeKey === 'nonTaxSaleInvoice';
  let footRows = '';
  if (showTotalsRow && lines.length > 0) {
    const totalQty = lines.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
    const totalDisc = lines.reduce((s, l) => s + (Number(l.discountAmount) || 0), 0);
    const totalTax = lines.reduce((s, l) => s + (Number(l.taxAmount) || 0), 0);
    const qtyStr = totalQty.toLocaleString(undefined, { maximumFractionDigits: 2 });
    const fb = 'font-weight:bold';

    // Leading label spans every column up to (but not including) Qty.
    let lead = 1;              // '#'
    if (showSrc) lead += 2;    // Src #, Src Date
    lead += 1;                 // Description
    if (showHs) lead += 1;
    if (showSize) lead += 2;   // Size, Pieces
    if (showBarcode) lead += 1;

    let f = `<td colspan="${lead}" style="text-align:right;${fb}">Total</td>`;
    f += `<td style="text-align:right;${fb}">${qtyStr}</td>`;                                  // Qty
    if (showUom) f += '<td></td>';
    if (showRate) f += '<td></td>';
    if (showDisc) f += `<td></td><td style="text-align:right;${fb}">${totalDisc ? formatAmount(totalDisc) : '-'}</td>`;
    if (showTax) f += `<td></td><td style="text-align:right;${fb}">${totalTax ? formatAmount(totalTax) : '-'}</td>`;
    if (showMrp) f += '<td></td>';
    f += '<td></td>';          // final Total column left blank (grand total shows below)
    footRows = `<tr style="border-top:2px solid #999">${f}</tr>`;
  }

  // Column count is read back off the head we just built rather than re-deriving
  // it from the toggle arithmetic above — one source of truth, so a future column
  // can never silently break the colspans below.
  const colCount = (head.match(/<th/g) || []).length;

  // Group summary rows (Quotation "Split Stores & Services"): a right-aligned
  // label spanning every column except the last, with the figure sitting under
  // the Total column. Only that one feature passes these, so every other
  // document prints an unchanged table.
  if (Array.isArray(opts.summaryRows) && opts.summaryRows.length > 0) {
    footRows += opts.summaryRows.map((r, i) => {
      const w = r.bold ? 'font-weight:bold;' : '';
      const top = i === 0 ? 'border-top:2px solid #999;' : '';
      return `<tr><td colspan="${Math.max(1, colCount - 1)}" style="text-align:right;${w}${top}">${r.label}</td>`
        + `<td style="text-align:right;${w}${top}">${r.value}</td></tr>`;
    }).join('');
  }

  // display:table-row-group — print the total row(s) ONCE, after the last item.
  // A plain <tfoot> is repeated by the browser at the bottom of every printed
  // page, so a two-page invoice showed the Total row on page 1 as well.
  const footer = footRows ? `<tfoot style="display:table-row-group">${footRows}</tfoot>` : '';

  // Optional heading row inside the table body (e.g. "Stores" / "Services"), so
  // the group label sits within the same bordered box as its rows.
  const titleRow = opts.sectionTitle
    ? `<tr><td colspan="${colCount}" style="font-weight:bold;text-decoration:underline">${escHtml(opts.sectionTitle)}</td></tr>`
    : '';

  return `<table class="items"><thead><tr>${head}</tr></thead><tbody>${titleRow}${rows}</tbody>${footer}</table>`;
}

// ─── Quotation: Stores / Services split ──────────────────────────────────────
// Opt-in per company (Print Settings → Quotation → "Split Stores & Services").
// Renders product lines as a "Stores" table and service lines as a "Services"
// table, each closing with its own sub-total and its own tax, and the grand
// total in the last block's footer — the layout contractors here already quote
// on, where goods carry GST and services carry provincial SST at a different
// rate. Costs no extra Firestore reads: `itemType` is already stamped on every
// saved line by enrichTransactionLines(), so this is pure in-memory grouping.
function buildSplitStoresServicesTables(lines, cols, docTypeKey, data, currency) {
  const all = lines || [];
  if (all.length === 0) return buildStandardTable(all, cols, docTypeKey);

  // Quotations saved before enrichTransactionLines() stamped itemType carry no
  // tag at all. Those fall to Stores rather than disappearing — an old reprint
  // then looks like today's single table instead of losing rows.
  const storeLines = all.filter(l => !(l && l.itemType === 'service'));
  const serviceLines = all.filter(l => l && l.itemType === 'service');

  // lineTotal INCLUDES tax (Quotation.calculateLine: net + tax), so the pre-tax
  // figure is lineTotal minus the line's own tax. taxAmount is already 0 on
  // non-taxable lines, so it needs no separate `taxable` check.
  const sumNet = (ls) => ls.reduce((s, l) => s + ((Number(l.lineTotal) || 0) - (Number(l.taxAmount) || 0)), 0);
  const sumTax = (ls) => ls.reduce((s, l) => s + (Number(l.taxAmount) || 0), 0);

  const storeNet = sumNet(storeLines);
  const storeTax = sumTax(storeLines);
  const storeTotal = storeNet + storeTax;
  const svcNet = sumNet(serviceLines);
  const svcTax = sumTax(serviceLines);

  // Document-level discount is not apportioned between the two groups (that
  // would be an accounting decision, not a print one) — it stays a single
  // deduction immediately above the grand total.
  const headerDisc = Number(data.discountAmount) || 0;
  const grandTotal = storeTotal + svcNet + svcTax - headerDisc;

  const bothGroups = storeLines.length > 0 && serviceLines.length > 0;

  // "GST @ 18%" when every taxed line in the group shares one rate, otherwise
  // the bare name — mixed rates would make a single percentage a lie.
  const taxRowLabel = (name, ls) => {
    const rates = new Set();
    ls.forEach(l => { if (l && Number(l.taxAmount) > 0 && Number(l.taxRate) > 0) rates.add(Number(l.taxRate)); });
    return rates.size === 1 ? `${name} @ ${[...rates][0]}%` : name;
  };

  const grandRows = () => {
    const r = [];
    if (headerDisc) r.push({ label: 'Discount', value: `-${formatAmount(headerDisc)}` });
    r.push({ label: 'Grand Total', value: `${currency} ${formatAmount(grandTotal)}`, bold: true });
    return r;
  };

  const tables = [];

  if (storeLines.length > 0) {
    const rows = [{ label: 'Sub Total', value: formatAmount(storeNet) }];
    if (storeTax) rows.push({ label: taxRowLabel('GST', storeLines), value: formatAmount(storeTax) });
    // With services following, Stores closes on its own total and the grand
    // total is carried down into the Services footer. With no services at all,
    // this IS the closing block, so the grand total belongs here instead.
    if (bothGroups) rows.push({ label: 'Total', value: formatAmount(storeTotal), bold: true });
    else rows.push(...grandRows());
    tables.push(buildStandardTable(storeLines, cols, docTypeKey, {
      colSourceLines: all, sectionTitle: 'Stores', summaryRows: rows
    }));
  }

  if (serviceLines.length > 0) {
    const rows = [{ label: 'Sub Total', value: formatAmount(svcNet) }];
    if (bothGroups) rows.push({ label: 'Total Store Cost', value: formatAmount(storeTotal) });
    if (svcTax) rows.push({ label: taxRowLabel('SST', serviceLines), value: formatAmount(svcTax) });
    rows.push(...grandRows());
    tables.push(buildStandardTable(serviceLines, cols, docTypeKey, {
      colSourceLines: all, sectionTitle: 'Services', summaryRows: rows,
      numberFrom: storeLines.length
    }));
  }

  return tables.join('');
}

function buildGRNTable(lines) {
  // Size columns appear only when at least one line is a size item (mirrors buildStandardTable).
  const showSize = (lines || []).some(l => l && l.hasSize);
  let head = '<th>#</th><th>Description</th>';
  if (showSize) head += '<th>Size</th><th style="text-align:right">Pieces</th>';
  head += '<th>Unit</th><th style="text-align:right">Ordered</th><th style="text-align:right">Received</th><th style="text-align:right">Accepted</th><th style="text-align:right">Rejected</th><th style="text-align:right">Unit Cost</th><th style="text-align:right">Total</th>';
  const rows = lines.map((l, i) => {
    const sizeCells = showSize
      ? `<td style="white-space:nowrap">${l.sizeLabel || (l.hasSize && l.length && l.height ? `${l.length}×${l.height}${l.thickness ? '×' + l.thickness : ''}` : '-')}</td><td style="text-align:right">${l.hasSize ? (l.pieces || 0) : '-'}</td>`
      : '';
    return `<tr><td>${i + 1}</td><td>${l.description || ''}</td>${sizeCells}<td>${l.unitOfMeasure || ''}</td><td style="text-align:right">${l.orderedQty || '-'}</td><td style="text-align:right">${qtyCell({ ...l, quantity: l.receivedQty })}</td><td style="text-align:right">${l.acceptedQty}</td><td style="text-align:right">${l.rejectedQty || 0}</td><td style="text-align:right">${formatAmount(l.unitCost)}</td><td style="text-align:right">${formatAmount(l.lineTotal)}</td></tr>`;
  }).join('');
  return `<table class="items"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>`;
}

function buildDCTable(lines) {
  // DC = dispatch note. Rate/Amount are not relevant on the printed challan
  // (they belong on the SI). Show only Batch / Pack Size when populated.
  const showSize = (lines || []).some(l => l && l.hasSize);
  const showBatch = lines.some(l => l.batch && String(l.batch).trim());
  const showPack = lines.some(l => l.packSize && String(l.packSize).trim());
  // HS Code (2026-11-02): only when a line carries one.
  const showHs = lines.some(l => l && l.hsCode);
  let head = '<th>#</th><th>Description</th>';
  if (showHs) head += '<th>HS Code</th>';
  if (showSize) head += '<th>Size</th><th style="text-align:right">Pieces</th>';
  head += '<th>Unit</th>';
  if (showBatch) head += '<th>Batch</th>';
  if (showPack) head += '<th>Pack Size</th>';
  head += '<th style="text-align:right">Ordered Qty</th><th style="text-align:right">Delivered Qty</th>';
  const rows = lines.map((l, i) => {
    let row = `<td>${i + 1}</td><td>${itemThumb(l)}${l.description || ''}</td>`;
    if (showHs) row += `<td>${l.hsCode || ''}</td>`;
    if (showSize) row += `<td style="white-space:nowrap">${l.sizeLabel || (l.hasSize && l.length && l.height ? `${l.length}×${l.height}${l.thickness ? '×' + l.thickness : ''}` : '-')}</td><td style="text-align:right">${l.hasSize ? (l.pieces || 0) : '-'}</td>`;
    row += `<td>${l.unitOfMeasure || ''}</td>`;
    if (showBatch) row += `<td>${l.batch || ''}</td>`;
    if (showPack) row += `<td>${l.packSize || ''}</td>`;
    row += `<td style="text-align:right">${l.orderedQty || '-'}</td><td style="text-align:right">${qtyCell(l)}</td>`;
    return `<tr>${row}</tr>`;
  }).join('');
  return `<table class="items"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>`;
}

function buildReturnTable(lines, cols, docTypeKey) {
  const showTax = cols.tax !== false;
  // Size mode columns mirror buildStandardTable: appear only when at least one
  // line on the return carries the hasSize flag. For mixed returns, non-size
  // lines render "-" in those columns.
  const showSize = (lines || []).some(l => l && l.hasSize);
  // HS Code on the Sale Return print (2026-11-02): only when a line carries one.
  const showHs = docTypeKey === 'saleReturn' && cols.hsCode !== false && (lines || []).some(l => l && l.hsCode);

  let head = '<th>#</th><th>Description</th>';
  if (showHs) head += '<th>HS Code</th>';
  if (showSize) head += '<th>Size</th><th style="text-align:right">Pieces</th>';
  head += '<th>Unit</th><th style="text-align:right">Orig. Qty</th><th style="text-align:right">Return Qty</th><th style="text-align:right">Unit Price</th>';
  if (showTax) head += '<th style="text-align:right">Tax %</th><th style="text-align:right">Tax Amt</th>';
  head += '<th style="text-align:right">Total</th>';

  const rows = lines.map((l, i) => {
    const returnQty = docTypeKey === 'purchaseReturn' ? (l.returnQty || l.quantity) : l.quantity;
    const taxPct = Number(l.taxRate) || 0;
    const taxAmt = Number(l.taxAmount) || 0;
    let row = `<td>${i + 1}</td><td>${itemThumb(l)}${l.description || ''}</td>`;
    if (showHs) row += `<td>${l.hsCode || ''}</td>`;
    if (showSize) {
      const sizeLabel = l.sizeLabel || (l.hasSize && l.length && l.height
        ? `${l.length}×${l.height}${l.thickness ? '×' + l.thickness : ''}`
        : '-');
      row += `<td style="white-space:nowrap">${sizeLabel}</td>`;
      row += `<td style="text-align:right">${l.hasSize ? (l.pieces || 0) : '-'}</td>`;
    }
    row += `<td>${l.unitOfMeasure || ''}</td><td style="text-align:right">${l.originalQty || '-'}</td><td style="text-align:right">${qtyCell({ ...l, quantity: returnQty })}</td><td style="text-align:right">${formatAmount(l.unitPrice)}</td>`;
    if (showTax) {
      row += `<td style="text-align:right">${l.taxable && taxPct ? taxPct + '%' : '-'}</td>`;
      row += `<td style="text-align:right">${l.taxable ? formatAmount(taxAmt) : '-'}</td>`;
    }
    row += `<td style="text-align:right">${formatAmount(l.lineTotal)}</td>`;
    return `<tr>${row}</tr>`;
  }).join('');

  return `<table class="items"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>`;
}

// ─── Store Module Table Builders ─────────────────────────────────────────────

function buildStockAdjustTable(lines) {
  const head = '<th>#</th><th>Description</th><th>Unit</th><th style="text-align:right">Qty</th><th style="text-align:right">Unit Cost</th><th style="text-align:right">Total</th>';
  const rows = lines.map((l, i) =>
    `<tr><td>${i + 1}</td><td>${l.description || l.itemName || ''}</td><td>${l.unitOfMeasure || ''}</td><td style="text-align:right">${l.quantity}</td><td style="text-align:right">${formatAmount(l.unitCost || l.unitPrice || 0)}</td><td style="text-align:right">${formatAmount(l.lineTotal || (l.quantity * (l.unitCost || l.unitPrice || 0)))}</td></tr>`
  ).join('');
  return `<table class="items"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>`;
}

function buildRequisitionTable(lines) {
  const head = '<th>#</th><th>Description</th><th>Unit</th><th style="text-align:right">Qty</th><th style="text-align:right">Est. Price</th><th style="text-align:right">Total</th><th>Preferred Vendor</th>';
  const rows = lines.map((l, i) =>
    `<tr><td>${i + 1}</td><td>${l.description || l.itemName || ''}</td><td>${l.unitOfMeasure || ''}</td><td style="text-align:right">${l.quantity}</td><td style="text-align:right">${formatAmount(l.estimatedPrice || 0)}</td><td style="text-align:right">${formatAmount(l.lineTotal || (l.quantity * (l.estimatedPrice || 0)))}</td><td>${l.preferredVendorName || '-'}</td></tr>`
  ).join('');
  return `<table class="items"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>`;
}

function buildGatePassTable(lines) {
  const head = '<th>#</th><th>Description</th><th>Unit</th><th style="text-align:right">Qty</th><th>Remarks</th>';
  const rows = lines.map((l, i) =>
    `<tr><td>${i + 1}</td><td>${l.description || l.itemName || ''}</td><td>${l.unitOfMeasure || l.uom || ''}</td><td style="text-align:right">${l.quantity}</td><td>${l.remarks || '-'}</td></tr>`
  ).join('');
  return `<table class="items"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>`;
}

function buildBOMTable(lines) {
  const head = '<th>#</th><th>Component</th><th>Unit</th><th style="text-align:right">Qty</th><th style="text-align:right">Scrap %</th><th style="text-align:center">Critical</th><th style="text-align:right">Unit Cost</th><th style="text-align:right">Line Cost</th>';
  const rows = lines.map((l, i) =>
    `<tr><td>${i + 1}</td><td>${l.itemCode ? l.itemCode + ' - ' : ''}${l.description || l.itemName || ''}</td><td>${l.unitOfMeasure || l.uom || ''}</td><td style="text-align:right">${l.quantity}</td><td style="text-align:right">${l.scrapPercent || 0}%</td><td style="text-align:center">${l.isCritical ? 'Yes' : '-'}</td><td style="text-align:right">${formatAmount(l.unitCost || 0)}</td><td style="text-align:right">${formatAmount(l.lineCost || 0)}</td></tr>`
  ).join('');
  return `<table class="items"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>`;
}

function buildProductionTable(lines) {
  const head = '<th>#</th><th>Component</th><th>Unit</th><th style="text-align:center">Crit</th><th style="text-align:right">Est. Qty</th><th style="text-align:right">Est. Cost</th><th style="text-align:right">Act. Qty</th><th style="text-align:right">Act. Cost</th><th style="text-align:right">Variance</th>';
  const rows = lines.map((l, i) => {
    const variance = (l.actualLineCost || 0) - (l.estLineCost || 0);
    return `<tr><td>${i + 1}</td><td>${l.itemCode ? l.itemCode + ' - ' : ''}${l.description || l.itemName || ''}</td><td>${l.unitOfMeasure || l.uom || ''}</td><td style="text-align:center">${l.isCritical ? '!' : '-'}</td><td style="text-align:right">${l.estQty || 0}</td><td style="text-align:right">${formatAmount(l.estLineCost || 0)}</td><td style="text-align:right">${l.actualQty || 0}</td><td style="text-align:right">${formatAmount(l.actualLineCost || 0)}</td><td style="text-align:right;color:${variance > 0 ? 'red' : 'green'}">${formatAmount(variance)}</td></tr>`;
  }).join('');
  return `<table class="items"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>`;
}

// ─── Build Totals ─────────────────────────────────────────────────────────────

// Return "Tax (17%)" when all taxable lines share the same rate, else "Tax".
function taxLabelForLines(lines) {
  const rates = new Set();
  (lines || []).forEach(l => {
    if (l && l.taxable !== false && Number(l.taxRate) > 0) rates.add(Number(l.taxRate));
  });
  if (rates.size === 1) return `Tax (${[...rates][0]}%)`;
  return 'Tax';
}

// Purchase Order / Purchase Invoice / Non-Tax Purchase Invoice: when lines carry
// a discount, "Gross Amount" and "Line Discount" print above the Subtotal (which
// is already after line discounts). Other documents, purchases without a line
// discount, or the Discount column switched off in Print Settings: nothing.
const LINE_DISCOUNT_SUMMARY_DOCS = new Set(['purchaseOrder', 'purchaseInvoice', 'nonTaxPurchaseInvoice']);
function lineDiscountRows(docTypeKey, data, config) {
  if (!LINE_DISCOUNT_SUMMARY_DOCS.has(docTypeKey) || config.showDiscount === false) return [];
  const { gross, lineDiscount } = lineDiscountSummary(data.lines);
  if (!(lineDiscount > 0)) return [];
  return [
    `<tr><td>Gross Amount</td><td style="text-align:right">${formatAmount(gross)}</td></tr>`,
    `<tr><td>Line Discount</td><td style="text-align:right">-${formatAmount(lineDiscount)}</td></tr>`,
  ];
}

function buildTotals(docTypeKey, data, config, activeCompany) {
  const meta = DOC_META[docTypeKey];
  const currency = activeCompany?.currencyCode || data.currency || 'PKR';
  const taxLabel = taxLabelForLines(data.lines);
  // Sale-Invoice-only custom WHT label + direction (company settings).
  const whtLbl = (docTypeKey === 'saleInvoice' && (activeCompany?.whtLabel || '').trim()) || 'WHT';

  // Quotation "Split Stores & Services" prints Sub Total / tax / Grand Total
  // inside the group tables themselves, so a second totals block underneath
  // would just repeat the same figures.
  if (docTypeKey === 'quotation' && config.splitStoresServices) {
    return '';
  }

  if (meta.totalsType === 'none') {
    return '';
  }

  if (meta.totalsType === 'simple') {
    // Bank/Cash RECEIPT with per-invoice WHT / write-off: totalAmount is the CASH
    // received and gross = cash + WHT + bad debt, so print Gross → WHT →
    // Bad Debt → Net Received. Keyed on the doc type: payments keep the branch below.
    const recAdj = Number(data.adjustmentAmount) || 0;
    if (isReceiptDoc(data) && (Number(data.whtAmount) > 0 || recAdj > 0)) {
      return `<div class="totals"><table>
        <tr><td>Total Amount (gross)</td><td style="text-align:right">${currency} ${formatAmount(receiptGrossOf(data))}</td></tr>
        ${Number(data.whtAmount) > 0 ? `<tr><td>WHT deducted by customer</td><td style="text-align:right">-${formatAmount(data.whtAmount)}</td></tr>` : ''}
        ${recAdj > 0 ? `<tr><td>Adjustment / Bad Debt</td><td style="text-align:right">-${formatAmount(recAdj)}</td></tr>` : ''}
        <tr class="grand-total"><td><strong>Net Received</strong></td><td style="text-align:right"><strong>${currency} ${formatAmount(data.totalAmount || 0)}</strong></td></tr>
      </table></div>`;
    }
    // When WHT / an adjustment was withheld (party / multi-party payments), show
    // Total → WHT → Adjustment → Net. Net is DERIVED (gross − WHT − adjustment):
    // the stored netAmount is overwritten with the gross by the save code.
    // Plain vouchers (ADV/LON, receipts without deductions) keep the single total.
    const payAdj = Number(data.adjustmentAmount) || 0;
    if (Number(data.whtAmount) > 0 || payAdj > 0) {
      const perInvoiceWht = (data.invoiceAllocations || []).some(a => Number(a.whtAmount) > 0);
      const payNet = Math.round(((Number(data.totalAmount) || 0) - (Number(data.whtAmount) || 0) - payAdj) * 100) / 100;
      return `<div class="totals"><table>
        <tr><td>Total Amount</td><td style="text-align:right">${currency} ${formatAmount(data.totalAmount || 0)}</td></tr>
        ${Number(data.whtAmount) > 0 ? `<tr><td>WHT${data.whtRate && !perInvoiceWht ? ` (${data.whtRate}%)` : ''}</td><td style="text-align:right">-${formatAmount(data.whtAmount)}</td></tr>` : ''}
        ${payAdj > 0 ? `<tr><td>Adjustment / Discount</td><td style="text-align:right">-${formatAmount(payAdj)}</td></tr>` : ''}
        <tr class="grand-total"><td><strong>Net Amount</strong></td><td style="text-align:right"><strong>${currency} ${formatAmount(payNet)}</strong></td></tr>
      </table></div>`;
    }
    return `<div style="text-align:right;font-size:16px;font-weight:bold;color:${config.accentColor}">Total: ${currency} ${formatAmount(data.totalAmount || 0)}</div>`;
  }

  const rows = [];

  if (meta.totalsType === 'dc') {
    // Delivery Challan is a dispatch note — no money totals printed.
    return '';
  } else if (meta.totalsType === 'invoice') {
    // Optional "Total Quantity" row — sum of every line's qty (off by default).
    if (config.showTotalQty) {
      const totQty = (data.lines || []).reduce((s, l) => s + (Number(l.quantity) || 0), 0);
      const qtyStr = Number.isInteger(totQty) ? String(totQty) : totQty.toFixed(2);
      rows.push(`<tr><td>Total Quantity</td><td style="text-align:right">${qtyStr}</td></tr>`);
    }
    if (config.showSubtotal !== false) {
      rows.push(...lineDiscountRows(docTypeKey, data, config));
      rows.push(`<tr><td>Subtotal</td><td style="text-align:right">${formatAmount(data.subtotal)}</td></tr>`);
    }
    if (config.showDiscount !== false && data.discountAmount) rows.push(`<tr><td>Discount</td><td style="text-align:right">-${formatAmount(data.discountAmount)}</td></tr>`);
    if (config.showTax !== false && data.taxAmount) rows.push(`<tr><td>${taxLabel}</td><td style="text-align:right">${formatAmount(data.taxAmount)}</td></tr>`);
    if (config.showWHT && data.whtAmount) {
      const whtSign = (docTypeKey === 'saleInvoice' && data.whtAddToTotal) ? '' : '-';
      rows.push(`<tr><td>${whtLbl} (${data.whtRate || 0}%)</td><td style="text-align:right">${whtSign}${formatAmount(data.whtAmount)}</td></tr>`);
    }
    // Non-taxable charges recovered on Sale Invoices (only present on SI/NSI; purchase
    // invoices share this block but never carry these fields, so nothing renders there).
    if (config.showTransportation !== false && Number(data.transportationCharges) > 0) rows.push(`<tr><td>Transportation / Carriage</td><td style="text-align:right">${formatAmount(data.transportationCharges)}</td></tr>`);
    if (config.showOtherCharges !== false && Number(data.otherCharges) > 0) rows.push(`<tr><td>Other Charges</td><td style="text-align:right">${formatAmount(data.otherCharges)}</td></tr>`);
    rows.push(`<tr class="grand-total"><td><strong>Total</strong></td><td style="text-align:right"><strong>${currency} ${formatAmount(data.totalAmount)}</strong></td></tr>`);
    if (config.showPaymentInfo && config.showAdvancePayment !== false) {
      const isSale = docTypeKey === 'saleInvoice' || docTypeKey === 'nonTaxSaleInvoice';
      rows.push(`<tr><td>${isSale ? 'Advance Payment' : 'Paid'}</td><td style="text-align:right">${formatAmount(data.paidAmount || 0)}</td></tr>`);
      rows.push(`<tr class="grand-total"><td><strong>Balance</strong></td><td style="text-align:right"><strong>${formatAmount(data.balanceAmount ?? data.totalAmount)}</strong></td></tr>`);
    }
    // Optional account block for credit customers: what they owed BEFORE this
    // bill, this bill, what they paid, and the resulting balance. previousBalance
    // is supplied by the caller (Sale Invoice print) only when the toggle is on.
    if (config.showPreviousBalance && data.previousBalance != null && !Number.isNaN(Number(data.previousBalance))) {
      const prev = Number(data.previousBalance) || 0;
      const bill = Number(data.totalAmount) || 0;
      const paid = Number(data.paidAmount) || 0;
      rows.push(`<tr><td colspan="2" style="padding-top:6px;border-top:1px dashed #999;font-weight:600">Account Balance</td></tr>`);
      rows.push(`<tr><td>Previous Balance</td><td style="text-align:right">${formatAmount(prev)}</td></tr>`);
      rows.push(`<tr><td>This Bill</td><td style="text-align:right">${formatAmount(bill)}</td></tr>`);
      rows.push(`<tr><td>Paid</td><td style="text-align:right">-${formatAmount(paid)}</td></tr>`);
      rows.push(`<tr class="grand-total"><td><strong>New Balance</strong></td><td style="text-align:right"><strong>${formatAmount(prev + bill - paid)}</strong></td></tr>`);
    }
    // Optional amount-in-words line under the totals.
    if (config.showAmountInWords) {
      rows.push(`<tr><td colspan="2" style="padding-top:6px;border-top:1px dashed #999;font-style:italic">${escHtml(currency)} ${escHtml(amountToWords(data.totalAmount))}</td></tr>`);
    }
  } else if (meta.totalsType === 'order') {
    rows.push(...lineDiscountRows(docTypeKey, data, config));
    rows.push(`<tr><td>Subtotal</td><td style="text-align:right">${formatAmount(data.subtotal)}</td></tr>`);
    if (config.showDiscount !== false && data.discountAmount) rows.push(`<tr><td>Discount</td><td style="text-align:right">-${formatAmount(data.discountAmount)}</td></tr>`);
    if (config.showTax !== false && data.taxAmount) rows.push(`<tr><td>${taxLabel}</td><td style="text-align:right">${formatAmount(data.taxAmount)}</td></tr>`);
    rows.push(`<tr class="grand-total"><td><strong>Total</strong></td><td style="text-align:right"><strong>${currency} ${formatAmount(data.totalAmount)}</strong></td></tr>`);
  } else if (meta.totalsType === 'return' || meta.totalsType === 'returnWht') {
    rows.push(`<tr><td>Subtotal</td><td style="text-align:right">${formatAmount(data.subtotal)}</td></tr>`);
    if (config.showTax !== false && data.taxAmount) rows.push(`<tr><td>${taxLabel}</td><td style="text-align:right">${formatAmount(data.taxAmount)}</td></tr>`);
    if (meta.totalsType === 'returnWht' && config.showWHT && data.whtAmount > 0) {
      rows.push(`<tr><td>WHT (${data.whtRate || 0}%)</td><td style="text-align:right">-${formatAmount(data.whtAmount)}</td></tr>`);
    }
    rows.push(`<tr class="grand-total"><td>Total Return</td><td style="text-align:right">${currency} ${formatAmount(data.totalAmount)}</td></tr>`);
  } else if (meta.totalsType === 'bomCost') {
    rows.push(`<tr><td>Material Cost</td><td style="text-align:right">${formatAmount(data.materialCost || 0)}</td></tr>`);
    rows.push(`<tr><td>Overhead Cost</td><td style="text-align:right">${formatAmount(data.overheadCost || 0)}</td></tr>`);
    rows.push(`<tr><td>Outsourcing Cost</td><td style="text-align:right">${formatAmount(data.outsourcingCost || 0)}</td></tr>`);
    rows.push(`<tr class="grand-total"><td><strong>Total Cost</strong></td><td style="text-align:right"><strong>${currency} ${formatAmount(data.totalCost || 0)}</strong></td></tr>`);
    rows.push(`<tr class="grand-total"><td><strong>Cost Per Unit</strong></td><td style="text-align:right"><strong>${currency} ${formatAmount(data.costPerUnit || 0)}</strong></td></tr>`);
  } else if (meta.totalsType === 'import') {
    const fc = data.foreignCurrency || 'USD';
    rows.push(`<tr><td>Foreign Subtotal (${fc})</td><td style="text-align:right">${(data.foreignSubtotal || 0).toLocaleString()}</td></tr>`);
    rows.push(`<tr><td>× Exchange Rate</td><td style="text-align:right">${data.exchangeRate || 0}</td></tr>`);
    rows.push(`<tr><td><strong>= ${currency} Subtotal (Vendor Payable)</strong></td><td style="text-align:right"><strong>${formatAmount(data.baseSubtotal || data.subtotal || 0)}</strong></td></tr>`);
    if (data.capitalizedTotal) {
      rows.push(`<tr><td>+ Capitalized Charges</td><td style="text-align:right;color:${config.accentColor}">+${formatAmount(data.capitalizedTotal)}</td></tr>`);
    }
    rows.push(`<tr><td><strong>= Inventory Value (Dr Inventory)</strong></td><td style="text-align:right"><strong>${formatAmount(data.inventoryValue || 0)}</strong></td></tr>`);
    if (data.nonCapitalizedTotal) {
      rows.push(`<tr><td>+ Non-Capitalized (claimable)</td><td style="text-align:right">+${formatAmount(data.nonCapitalizedTotal)}</td></tr>`);
    }
    rows.push(`<tr class="grand-total"><td><strong>Grand Total Owed</strong></td><td style="text-align:right"><strong>${currency} ${formatAmount(data.totalAmount)}</strong></td></tr>`);
    if (config.showPaymentInfo) {
      rows.push(`<tr><td>Paid</td><td style="text-align:right">${formatAmount(data.paidAmount || 0)}</td></tr>`);
      rows.push(`<tr class="grand-total"><td><strong>Balance</strong></td><td style="text-align:right"><strong>${formatAmount(data.balanceAmount ?? data.totalAmount)}</strong></td></tr>`);
    }
  } else if (meta.totalsType === 'payslip') {
    const l = data.line || {};
    rows.push(`<tr class="grand-total" style="font-size:16px"><td><strong>NET PAYABLE</strong></td><td style="text-align:right"><strong>${currency} ${formatAmount(l.netPayable || 0)}</strong></td></tr>`);
  } else if (meta.totalsType === 'journal') {
    // Journal voucher — table already shows Dr/Cr totals; here we just assert balance visually
    const dr = Number(data.totalDebit || data.totalAmount) || 0;
    const cr = Number(data.totalCredit || data.totalAmount) || 0;
    const balanced = Math.abs(dr - cr) <= 0.01;
    rows.push(`<tr class="grand-total"><td><strong>Balance Check</strong></td><td style="text-align:right;color:${balanced ? '#22c55e' : '#dc2626'}"><strong>${balanced ? 'Dr = Cr ✓' : `OUT OF BALANCE (${currency} ${formatAmount(dr - cr)})`}</strong></td></tr>`);
  } else if (meta.totalsType === 'productionCost') {
    rows.push(`<tr><td></td><td style="text-align:right;font-weight:bold">Estimated</td><td style="text-align:right;font-weight:bold">Actual</td><td style="text-align:right;font-weight:bold">Variance</td></tr>`);
    rows.push(`<tr><td>Material</td><td style="text-align:right">${formatAmount(data.estMaterialCost || 0)}</td><td style="text-align:right">${formatAmount(data.actualMaterialCost || 0)}</td><td style="text-align:right">${formatAmount(data.materialVariance || 0)}</td></tr>`);
    rows.push(`<tr><td>Overhead</td><td style="text-align:right">${formatAmount(data.estOverheadCost || 0)}</td><td style="text-align:right">${formatAmount(data.actualOverheadCost || 0)}</td><td style="text-align:right">${formatAmount(data.overheadVariance || 0)}</td></tr>`);
    rows.push(`<tr><td>Outsourcing</td><td style="text-align:right">${formatAmount(data.estOutsourcingCost || 0)}</td><td style="text-align:right">${formatAmount(data.actualOutsourcingCost || 0)}</td><td style="text-align:right">${formatAmount(data.outsourcingVariance || 0)}</td></tr>`);
    rows.push(`<tr class="grand-total"><td><strong>Total</strong></td><td style="text-align:right"><strong>${formatAmount(data.estTotalCost || 0)}</strong></td><td style="text-align:right"><strong>${formatAmount(data.actualTotalCost || 0)}</strong></td><td style="text-align:right"><strong>${formatAmount(data.totalVariance || 0)}</strong></td></tr>`);
    rows.push(`<tr class="grand-total"><td><strong>Cost/Unit</strong></td><td style="text-align:right"><strong>${formatAmount(data.estCostPerUnit || 0)}</strong></td><td style="text-align:right"><strong>${formatAmount(data.actualCostPerUnit || 0)}</strong></td><td></td></tr>`);
  }

  return `<div class="totals"><table>${rows.join('')}</table></div>`;
}

// Standalone Notes block — used when notesPosition === 'top' so callers can
// insert it above the line-item table. Returns '' when there's nothing to show.
// Inline style adjusts spacing so it sits cleanly between infoGrid and table.
function buildNotesBlock(data) {
  if (!data?.notes) return '';
  return `<div class="notes-section notes-top" style="margin-top:12px;margin-bottom:16px;">`
    + `<strong>Notes:</strong><p>${data.notes}</p></div>`;
}

// ─── Build Footer Sections ────────────────────────────────────────────────────

function buildFooterSections(docTypeKey, data, config) {
  const meta = DOC_META[docTypeKey];
  const parts = [];

  // Reason (for returns)
  if (data.reason && (docTypeKey === 'saleReturn' || docTypeKey === 'purchaseReturn' || docTypeKey === 'nonTaxSaleReturn' || docTypeKey === 'nonTaxPurchaseReturn')) {
    parts.push(`<div class="notes-section"><strong>Reason:</strong><p>${data.reason}</p></div>`);
  }

  // Terms & Conditions
  if (data.termsConditions) {
    parts.push(`<div class="notes-section"><strong>Terms & Conditions:</strong><p>${data.termsConditions}</p></div>`);
  }

  // Notes — rendered here only when the company has chosen 'bottom' (the default).
  // When notesPosition is 'top', the notes block is built separately by
  // buildNotesBlock() and inserted above the line-item table.
  if (data.notes && config.notesPosition !== 'top') {
    parts.push(`<div class="notes-section"><strong>Notes:</strong><p>${data.notes}</p></div>`);
  }

  // Signatures
  const showSigs = config.showSignatures || meta.defaultSignatures;
  if (showSigs) {
    const labels = meta.defaultSignatureLabels || config.signatureLabels || DEFAULT_GLOBAL.signatureLabels;
    const sigBlocks = labels.map(l => `<div class="sig-block"><div class="sig-line">${l}</div></div>`).join('');
    parts.push(`<div class="signatures">${sigBlocks}</div>`);
  }

  // Footer text. When letterhead has a footer image uploaded, the user's letterhead
  // image already carries the bottom branding (address bar, contact info), so the
  // built-in footer text would just duplicate that strip. Suppress it. If letterhead
  // is on but only header/watermark are uploaded (no footer image), keep the footerText
  // so users still get bottom-of-content notes like "Thank you for your business".
  if (config.footerText && !(config.letterhead && config.letterhead.footerUrl)) {
    parts.push(`<div class="footer-text">${config.footerText}</div>`);
  }

  return parts.join('');
}

// ─── Letterhead Layers ───────────────────────────────────────────────────────
// Renders the three fixed-position image layers (header / footer / watermark) and
// the CSS that flows content within their margins. Returns { html, css }.
function buildLetterheadLayers(lh) {
  if (!lh) return { html: '', css: '' };

  const layers = [];
  // Order matters for z-index fallback: watermark first (lowest), then header, then footer.
  if (lh.watermarkUrl) {
    layers.push(`<img class="lh-watermark" src="${lh.watermarkUrl}" alt="" />`);
  }
  if (lh.headerUrl) {
    layers.push(`<img class="lh-header" src="${lh.headerUrl}" alt="" />`);
  }
  if (lh.footerUrl) {
    layers.push(`<img class="lh-footer" src="${lh.footerUrl}" alt="" />`);
  }

  // Side margin is fixed at 15mm (matches the existing print-page margin) so content
  // doesn't hit the page edge horizontally. Top/bottom padding clears the fixed
  // header/footer images. Print-color-adjust forces all colors/images to print as-is
  // even when the user hasn't enabled "Background graphics" in the print dialog —
  // <img> tags always print, but this guards any decorative tints inside content.
  const topPad = lh.headerUrl ? `${lh.headerHeightMm}mm` : '15mm';
  const botPad = lh.footerUrl ? `${lh.footerHeightMm}mm` : '15mm';

  // Only the layers actually uploaded need their CSS rules; we also conditionally
  // override the built-in .header band only when the user's header image is in play.
  // This way "watermark-only" mode preserves the original template's coloured title.
  //
  // object-fit: fill (not contain) — fill stretches the image to occupy the entire
  // strip, so a mismatched aspect ratio doesn't render as a small letterboxed box
  // inside empty space. Users following the recommended dimensions get pixel-perfect
  // output; those who upload off-ratio images get a slightly stretched image, which
  // is a far better failure mode than a tiny image floating in white.
  const headerCSS = lh.headerUrl ? `
    .lh-header {
      position: fixed; top: 0; left: 0; right: 0;
      width: 100%; height: ${lh.headerHeightMm}mm;
      object-fit: fill;
      z-index: 2; pointer-events: none;
    }
    /* Modern template paints a full-bleed coloured band on .header — that bar would
       cover our header image, so neutralise its background. Horizontal padding is
       template-aware: in modern the header is a direct body child (needs its own
       15mm padding); in classic/detailed/compact it lives inside .print-page which
       already supplies the 15mm margin (so we collapse padding to 0). */
    .header.lh-doc-info { background: transparent !important; color: inherit !important; }
    body > .header.lh-doc-info { padding: 0 15mm !important; }
    .print-page > .header.lh-doc-info { padding: 0 !important; }
    .header.lh-doc-info .doc-title,
    .header.lh-doc-info .doc-number,
    .header.lh-doc-info .doc-meta { color: inherit !important; }
  ` : '';

  const footerCSS = lh.footerUrl ? `
    .lh-footer {
      position: fixed; bottom: 0; left: 0; right: 0;
      width: 100%; height: ${lh.footerHeightMm}mm;
      object-fit: fill;
      z-index: 2; pointer-events: none;
    }
  ` : '';

  const watermarkCSS = lh.watermarkUrl ? `
    .lh-watermark {
      position: fixed; top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      width: ${lh.watermarkWidthPct}%; height: auto; max-height: 70vh;
      object-fit: contain;
      opacity: ${lh.watermarkOpacity};
      z-index: 0; pointer-events: none;
    }
  ` : '';

  const css = `
    @page { margin: 0 !important; size: A4; }
    body { margin: 0 !important; padding: ${topPad} 0 ${botPad} 0 !important; }
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    /* Override the per-template page margins so content respects letterhead padding */
    .print-page { margin: 0 15mm !important; padding: 0 !important; position: relative; z-index: 1; background: transparent !important; }
    .body-content { padding: 0 15mm !important; position: relative; z-index: 1; background: transparent !important; }
    ${headerCSS}
    ${footerCSS}
    ${watermarkCSS}
  `;

  return { html: layers.join(''), css };
}

// ─── Paper size ────────────────────────────────────────────────────────────────
// A4 keeps the current look untouched (returns ''). A5 (portrait/landscape) sets
// the @page size and applies a compact scale so the invoice reads well on the
// smaller sheet; long invoices simply flow onto extra A5 pages.
function buildPaperCSS(paperSize) {
  if (paperSize !== 'A5P' && paperSize !== 'A5L') return '';
  const size = paperSize === 'A5L' ? '210mm 148mm' : '148mm 210mm';
  return `
    @page { size: ${size}; margin: 8mm; }
    @media print { body { margin: 8mm; } }
    body { font-size: 11px; }
    .company-name { font-size: 15px !important; }
    .company-detail { font-size: 9px !important; }
    .doc-title { font-size: 15px !important; }
    .doc-number { font-size: 12px !important; }
    .doc-meta { font-size: 10px !important; }
    .info-grid { gap: 6px !important; margin-bottom: 8px !important; }
    .info-item label { font-size: 9px !important; }
    .info-item span { font-size: 11px !important; }
    table.items th, table.items td { padding: 4px 6px !important; font-size: 10px !important; }
    .totals { width: 240px !important; }
    .totals td { padding: 3px 6px !important; font-size: 11px !important; }
    .notes-section { font-size: 11px !important; }
    .signatures { margin-top: 40px !important; }
    .sig-block { width: 130px !important; padding-top: 26px !important; }
    .sig-line { font-size: 10px !important; }
    .footer-text { font-size: 10px !important; }
  `;
}

// ─── Main Entry Point ─────────────────────────────────────────────────────────

// FBR POS block: the FBR fiscal invoice number, POS ID, a QR (generated from the
// fiscal number in the caller and passed as data.fbrQr), and a verify note. Shown
// on the Sale Invoice print only when the invoice has been posted to FBR.
function buildFbrPosBlock(data) {
  if (!data || !data.fbrInvoiceNumber) return '';
  const qrImg = data.fbrQr
    ? `<img src="${data.fbrQr}" alt="FBR QR" style="width:92px;height:92px;display:block;" />`
    : '';
  return `<div style="clear:both;margin-top:12px;padding:8px 12px;border:1.5px solid #000;border-radius:4px;display:inline-flex;align-items:center;gap:12px;max-width:380px;">
    ${qrImg}
    <div style="font-size:11px;line-height:1.55;color:#000;">
      <div style="font-weight:bold;font-size:12px;letter-spacing:.3px;">FBR POS INVOICE</div>
      <div>FBR Invoice&nbsp;#: <b>${data.fbrInvoiceNumber}</b></div>
      ${data.fbrPosId ? `<div>POS ID: ${data.fbrPosId}</div>` : ''}
      <div style="font-size:9px;color:#555;margin-top:2px;">Verify this invoice at fbr.gov.pk or the FBR Tax Asaan app.</div>
    </div>
  </div>`;
}

// FBR Digital Invoicing block: the official IRN + a QR (generated from the IRN in
// the caller as data.diQr) + a verify note. Shown only when the invoice has an
// IRN. Standalone — independent of the FBR POS block above.
function buildDiBlock(data) {
  if (!data || !data.diIrn) return '';
  const qrImg = data.diQr
    ? `<img src="${data.diQr}" alt="FBR QR" style="width:92px;height:92px;display:block;" />`
    : '';
  return `<div style="clear:both;margin-top:12px;padding:8px 12px;border:1.5px solid #000;border-radius:4px;display:inline-flex;align-items:center;gap:12px;max-width:380px;">
    ${qrImg}
    <div style="font-size:11px;line-height:1.55;color:#000;">
      <div style="font-weight:bold;font-size:12px;letter-spacing:.3px;">FBR DIGITAL INVOICE</div>
      <div>FBR IRN: <b>${data.diIrn}</b></div>
      <div style="font-size:9px;color:#555;margin-top:2px;">Verify this invoice at fbr.gov.pk or the FBR Tax Asaan app.</div>
    </div>
  </div>`;
}

export function printDocument(docTypeKey, data, activeCompany, savedConfig, activeBranch, opts = {}) {
  if (!data) return;

  const config = resolveConfig(docTypeKey, savedConfig);
  // Invoice Letterhead Choice (utils/invoiceLetterheads.js): another name's
  // details, or Blank, at the top instead of the company's own. The company's
  // uploaded letterhead images are its own branding, so they are left off too;
  // everything else (layout, paper, columns, totals, FBR block) stays the same.
  if (opts && opts.letterhead && (opts.letterhead.kind === 'blank' || opts.letterhead.kind === 'custom')) {
    config.altLetterhead = opts.letterhead;
    config.letterhead = null;
  }
  const css = getCSS(config.template, config.accentColor);
  const paperCSS = buildPaperCSS(config.paperSize);
  const isModern = config.template === 'modern';
  const letterheadLayers = buildLetterheadLayers(config.letterhead);

  const baseCurrency = activeCompany?.currencyCode || data.currency || 'PKR';
  const header = buildHeader(docTypeKey, data, activeCompany, config, activeBranch);
  const infoGrid = buildInfoGrid(docTypeKey, data, activeCompany, config);
  if (isRetailTradeLayout(docTypeKey, data, activeCompany)) config.retailTradeLayout = true;
  const table = buildTable(docTypeKey, data, config, baseCurrency);
  // IPI-only extra sections: charges + landed cost summary, between table and totals
  const extraSections = docTypeKey === 'importPurchaseInvoice'
    ? buildImportChargesSection(data, config.accentColor, baseCurrency) + buildLandedSummarySection(data, config.accentColor, baseCurrency)
    : '';
  const totals = buildTotals(docTypeKey, data, config, activeCompany);
  // FBR POS fiscal number + QR block — sale invoice / sale return, once posted to FBR.
  const fbrBlock = ((docTypeKey === 'saleInvoice' || docTypeKey === 'saleReturn') && data.fbrInvoiceNumber) ? buildFbrPosBlock(data) : '';
  const diBlock = ((docTypeKey === 'saleInvoice' || docTypeKey === 'saleReturn') && data.diIrn) ? buildDiBlock(data) : '';
  const footer = buildFooterSections(docTypeKey, data, config);
  // When the company has chosen Notes-above-items, this block goes between the
  // info grid and the items table. Otherwise it's empty (Notes lives in footer).
  const notesTop = config.notesPosition === 'top' ? buildNotesBlock(data) : '';

  // "Powered by AmalERP · www.amalerp.com" — fixed, not a print setting; only
  // Super Admin can switch it off for a company (see utils/printBranding.js).
  //   page       → bottom page margin of every printed page
  //   letterhead → just above the letterhead footer (no page margin there)
  //   halves     → bottom of each copy (two copies on one sheet)
  const brandMode = isPrintBrandHidden(activeCompany) ? null
    : config.letterhead ? 'letterhead'
    : config.duplicateCopy ? 'halves'
    : 'page';
  const brandCSS = brandMode === 'page' ? printBrandCss()
    : brandMode === 'letterhead' ? printBrandLetterheadCss(config.letterhead)
    : brandMode === 'halves' ? printBrandHalvesCss()
    : '';
  const brandHTML = brandMode === 'page' ? printBrandEndHtml()
    : brandMode === 'letterhead' ? printBrandLetterheadHtml()
    : '';
  const brandHalf = brandMode === 'halves' ? printBrandHalfHtml() : '';

  // Build single copy content
  const singleCopy = isModern
    ? `${header}<div class="body-content">${infoGrid}${notesTop}${table}${extraSections}${totals}${fbrBlock}${diBlock}${footer}</div>`
    : `<div class="print-page">${header}${infoGrid}${notesTop}${table}${extraSections}${totals}${fbrBlock}${diBlock}${footer}</div>`;

  let bodyContent;
  let extraCSS = '';

  if (config.duplicateCopy) {
    extraCSS = `
      @page { size: A4; margin: 5mm; }
      body { margin: 0; padding: 0; }
      .duplicate-wrapper { width: 100%; }
      .copy-half { height: 138mm; overflow: hidden; box-sizing: border-box; }
      .copy-half .print-page { margin: 6px 15px; }
      .copy-half .header { margin-bottom: 6px; padding-bottom: 5px; }
      .copy-half .company-name { font-size: 13px; }
      .copy-half .company-detail { font-size: 9px; margin: 1px 0; }
      .copy-half .doc-title { font-size: 14px; }
      .copy-half .doc-number { font-size: 12px; margin: 2px 0 0 0; }
      .copy-half .doc-meta { font-size: 10px; margin: 1px 0; }
      .copy-half .info-grid { margin-bottom: 6px; gap: 4px; }
      .copy-half .info-item label { font-size: 9px; }
      .copy-half .info-item span { font-size: 11px; }
      .copy-half table.items th { padding: 3px 5px; font-size: 10px; }
      .copy-half table.items td { padding: 3px 5px; font-size: 10px; }
      .copy-half .totals { margin-top: 4px; width: 260px; }
      .copy-half .totals td { padding: 2px 6px; font-size: 10px; }
      .copy-half .notes-section { margin-top: 5px; font-size: 10px; }
      .copy-half .notes-section strong { font-size: 10px; margin-bottom: 2px; }
      .copy-half .signatures { margin-top: 12px; }
      .copy-half .sig-block { width: 140px; }
      .copy-half .sig-line { font-size: 9px; padding-top: 3px; }
      .copy-half .footer-text { margin-top: 5px; font-size: 8px; padding-top: 4px; }
      ${isModern ? `.copy-half .header { padding: 10px 15px; }
        .copy-half .body-content { padding: 8px 15px; }
        .copy-half .info-grid { padding: 10px; }` : ''}
      .cut-line { border-top: 1px dashed #aaa; margin: 2mm 8mm; position: relative; height: 5mm; display: flex; align-items: center; justify-content: center; }
      .cut-line span { position: absolute; top: -7px; background: white; padding: 0 6px; font-size: 12px; color: #aaa; }
    `;

    const copyContent = isModern
      ? `${header}<div class="body-content">${infoGrid}${notesTop}${table}${extraSections}${totals}${fbrBlock}${footer}</div>`
      : `<div class="print-page">${header}${infoGrid}${notesTop}${table}${extraSections}${totals}${fbrBlock}${footer}</div>`;

    bodyContent = `
      <div class="duplicate-wrapper">
        <div class="copy-half">${copyContent}${brandHalf}</div>
        <div class="cut-line"><span>✂</span></div>
        <div class="copy-half">${copyContent}${brandHalf}</div>
      </div>`;
  } else {
    bodyContent = singleCopy;
  }

  // Letterhead layers (header/footer/watermark images) are siblings of the content
  // wrapper, not nested inside it. They use position: fixed so the browser repeats
  // them on every printed page automatically. The letterhead CSS is appended LAST so
  // it overrides the default per-template @page and body margins.
  const lhCSS = letterheadLayers.css;
  const lhHTML = letterheadLayers.html;

  const html = `<html><head><title>${data.docNumber}</title><style>${css}${paperCSS}${extraCSS}${lhCSS}${brandCSS}</style></head><body>${lhHTML}${bodyContent}${brandHTML}</body></html>`;

  // Print preview (Settings → Print Formats → "Show print preview", on unless
  // switched off): the SAME html opens in the in-app preview window with Print /
  // Download PDF. If the preview can't open, print the old way (new tab).
  const previewOn = savedConfig?.global?.printPreview !== false;
  if (previewOn) {
    // Space the printer adds around the page (page margin + print body margin),
    // so the on-screen sheet and the PDF look like the printed page.
    const pageMargin = config.letterhead ? null
      : config.duplicateCopy ? '5mm'
      : (config.paperSize === 'A5P' || config.paperSize === 'A5L') ? '16mm'
      : 'calc(15mm + 20px)';
    const heading = String(config.customTitle || DOC_META[docTypeKey]?.title || 'Document')
      .toLowerCase().replace(/\b\w/g, ch => ch.toUpperCase());   // "SALE INVOICE" → "Sale Invoice"
    const opened = openPrintPreview({
      html,
      docTypeKey,
      data,
      title: `${heading} ${data.docNumber || ''}`.trim(),
      fileName: data.docNumber || heading,
      paperSize: config.duplicateCopy ? 'A4' : (config.paperSize || 'A4'),
      pageMargin,
      letterhead: config.letterhead || null,
      brand: brandMode,
    });
    if (opened) return;
  }

  // Wait for every <img> in the new tab to finish loading before printing —
  // otherwise the letterhead images (and the company logo when not yet cached)
  // print blank (capped at 8 seconds; see printHtmlInNewWindow).
  printHtmlInNewWindow(html);
}

// ─── Firestore Loader ─────────────────────────────────────────────────────────

export async function loadDocumentFormats(companyId) {
  if (!companyId) return null;
  try {
    // Cached 2026-08-04 (Firebase cost): this getDoc ran on EVERY mount of
    // ~36 screens. The session cache serves it for 30 minutes at 0 reads.
    // The fetcher is left to throw so a failed read is never cached — the
    // catch below keeps the original "log and return null" behaviour.
    return await getCached(
      `docFormats|${companyId}`,
      async () => {
        const ref = doc(db, 'companies', companyId, 'settings', 'documentFormats');
        const snap = await getDoc(ref);
        return snap.exists() ? snap.data() : null;
      },
      30 * 60 * 1000
    );
  } catch (e) {
    console.error('Error loading print config:', e);
    return null;
  }
}
